import React, { useState, useEffect, useRef } from "react";
import AdminPanel from "./AdminPanel";
import WelcomeOverlay from "../components/WelcomeOverlay.jsx";
import GuestTips from "../components/GuestTips.jsx";
import GuestVoteSuccess from "../components/GuestVoteSuccess.jsx";
import EnhancedMarketRow from "../components/EnhancedMarketRow.jsx";
import EnhancedMarketModal from "../components/EnhancedMarketModal.jsx";

const colors = {
  bg: "#F8F7F4",
  card: "#FFF",
  border: "#E5E4E1",
  hover: "#F2F1EE",
  text: "#1A1A1A",
  textSec: "#6B6B6B",
  textTer: "#9B9B9B",
  green: "#16A34A",
  greenBg: "#F0FDF4",
  greenBorder: "#BBF7D0",
  red: "#DC2626",
  redBg: "#FEF2F2",
  redBorder: "#FECACA",
  blue: "#2563EB",
  blueBg: "#EFF6FF",
  blueBorder: "#BFDBFE",
};
const fonts = {
  display: "'Source Serif 4', serif",
  body: "'IBM Plex Sans', sans-serif",
  mono: "'IBM Plex Mono', monospace",
};
const DEFAULT_CATEGORIES = [
  { id: "all", label: "Всички", icon: "🔮", color: "#6B7280", bg_color: "#F3F4F6" },
];

// Global category cache — populated from API
let _categoriesCache = [];

function getCatLabel(catId) {
  const c = _categoriesCache.find(x => x.id === catId);
  return c ? c.label : catId;
}
function getCatColors(catId) {
  const c = _categoriesCache.find(x => x.id === catId);
  return c ? { bg: c.bg_color, color: c.color } : { bg: "#F3F4F6", color: "#6B7280" };
}
function getCat(catId) {
  return (
    _categoriesCache.find(x => x.id === catId) || {
      id: catId,
      label: catId,
      icon: "📌",
      color: "#6B7280",
      bg_color: "#F3F4F6",
    }
  );
}

// ── Tier System ──
const TIERS = [
  { id: "free", name: "Безплатен", icon: "🌱", threshold: 0, color: "#9B9B9B" },
  { id: "active", name: "Активен", icon: "⚡", threshold: 10, color: "#2563EB" },
  { id: "insider", name: "Инсайдер", icon: "🔑", threshold: 100, color: "#7C3AED" },
  { id: "oligarch", name: "Олигарх", icon: "💎", threshold: 500, color: "#D97706" },
];

function getUserTier(purchasedVotes) {
  for (let i = TIERS.length - 1; i >= 0; i--) {
    if (purchasedVotes >= TIERS[i].threshold) return TIERS[i];
  }
  return TIERS[0];
}

function hasAccess(user, tierId) {
  if (!user) return false;
  const tier = getUserTier(user.purchased_votes || 0);
  const tierOrder = { free: 0, active: 1, insider: 2, oligarch: 3 };
  return (tierOrder[tier.id] || 0) >= (tierOrder[tierId] || 0);
}

// ── Locked Overlay Component ──
function LockedOverlay({ requiredTier, user, onOpenShop, children, blurred }) {
  const has = hasAccess(user, requiredTier);
  if (has) return children;
  const tierInfo = TIERS.find(t => t.id === requiredTier);
  return (
    <div style={{ position: "relative" }}>
      <div
        style={{
          filter: blurred ? "blur(6px)" : "none",
          opacity: blurred ? 0.5 : 1,
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        {children}
      </div>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(248,247,244,0.7)",
          borderRadius: 8,
        }}
      >
        <div style={{ fontSize: 24, marginBottom: 6 }}>🔒</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: colors.text, textAlign: "center" }}>
          {tierInfo?.icon} {tierInfo?.name} ({tierInfo?.threshold}+ гласа)
        </div>
        <button
          onClick={onOpenShop}
          style={{
            marginTop: 8,
            fontSize: 12,
            fontWeight: 600,
            padding: "6px 14px",
            borderRadius: 6,
            border: "none",
            background: tierInfo?.color || colors.green,
            color: "#FFF",
            cursor: "pointer",
            fontFamily: fonts.body,
          }}
        >
          Отключи
        </button>
      </div>
    </div>
  );
}

const TURNSTILE_SITE_KEY = "Y0x4AAAAAACsCi8wEFjI4qmwQ";
const GOOGLE_CLIENT_ID = "829037765380-v0jl0enm5fbrr0qij7uqg8f3n9hb7r0i.apps.googleusercontent.com";

const LOGO_BLACK = "/BGPollBlack.png";
const LOGO_WHITE = "/BGPollWhite.png";

// ── Auth Modal (no Turnstile — growth mode) ──
function AuthModal({ mode, onClose, onAuth }) {
  const [isLogin, setIsLogin] = useState(mode === "login");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const i = setInterval(() => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogle,
        });
        const b = document.getElementById("google-signin-btn");
        if (b) {
          b.innerHTML = "";
          window.google.accounts.id.renderButton(b, {
            theme: "outline",
            size: "large",
            width: 352,
            text: isLogin ? "signin_with" : "signup_with",
            locale: "bg",
          });
        }
        clearInterval(i);
      }
    }, 200);
    return () => clearInterval(i);
  }, [isLogin]);
  async function handleGoogle(r) {
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken: r.credential }),
      });
      const d = await res.json();
      if (!res.ok) {
        setError(d.error || "Грешка");
        setLoading(false);
        return;
      }
      onAuth(d.user);
      onClose();
    } catch {
      setError("Мрежова грешка");
      setLoading(false);
    }
  }
  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const ep = isLogin ? "/api/auth/login" : "/api/auth/signup";
    const body = isLogin ? { email, password } : { email, username, password };
    try {
      const res = await fetch(ep, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) {
        setError(d.error || "Грешка");
        setLoading(false);
        return;
      }
      onAuth(d.user);
      onClose();
    } catch {
      setError("Мрежова грешка");
      setLoading(false);
    }
  }
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.4)",
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: colors.card,
          borderRadius: 12,
          padding: 32,
          width: "100%",
          maxWidth: 420,
          margin: "16px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={e => e.stopPropagation()}
      >
        <h2 style={{ fontFamily: fonts.display, fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
          {isLogin ? "Добре дошли отново" : "Създай акаунт"}
        </h2>
        <p style={{ fontSize: 14, color: colors.textSec, marginBottom: 20 }}>
          {isLogin
            ? "Влезте в своя BGPoll акаунт"
            : "Създайте акаунт, за да следите прогнозите си и се състезавате в класацията"}
        </p>
        <div
          id="google-signin-btn"
          style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}
        ></div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, height: 1, background: colors.border }}></div>
          <span style={{ fontSize: 12, color: colors.textTer }}>или с имейл</span>
          <div style={{ flex: 1, height: 1, background: colors.border }}></div>
        </div>
        {error && (
          <div
            style={{
              background: colors.redBg,
              border: `1px solid ${colors.redBorder}`,
              borderRadius: 8,
              padding: "8px 12px",
              marginBottom: 16,
              fontSize: 13,
              color: colors.red,
            }}
          >
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 12 }}>
            <label
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: colors.textSec,
                display: "block",
                marginBottom: 4,
              }}
            >
              Имейл
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: 14,
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                outline: "none",
                fontFamily: fonts.body,
                boxSizing: "border-box",
              }}
            />
          </div>
          {!isLogin && (
            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: colors.textSec,
                  display: "block",
                  marginBottom: 4,
                }}
              >
                Потребителско име
              </label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
                minLength={3}
                maxLength={20}
                pattern="[a-zA-Z0-9_]+"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: 14,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  outline: "none",
                  fontFamily: fonts.body,
                  boxSizing: "border-box",
                }}
                placeholder="букви, цифри, долна черта"
              />
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: colors.textSec,
                display: "block",
                marginBottom: 4,
              }}
            >
              Парола
            </label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={8}
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: 14,
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                outline: "none",
                fontFamily: fonts.body,
                boxSizing: "border-box",
              }}
              placeholder={isLogin ? "" : "Минимум 8 символа"}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "10px 16px",
              fontSize: 14,
              fontWeight: 600,
              color: "#FFF",
              background: colors.text,
              border: "none",
              borderRadius: 8,
              cursor: loading ? "not-allowed" : "pointer",
              fontFamily: fonts.body,
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "..." : isLogin ? "Вход" : "Създай акаунт"}
          </button>
        </form>
        <p style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: colors.textSec }}>
          {isLogin ? "Нямате акаунт? " : "Вече имате акаунт? "}
          <span
            style={{ color: colors.green, cursor: "pointer", fontWeight: 600 }}
            onClick={() => {
              setIsLogin(!isLogin);
              setError("");
            }}
          >
            {isLogin ? "Регистрация" : "Вход"}
          </span>
        </p>
      </div>
    </div>
  );
}

// ── Comments Section ──
function CommentsSection({ marketId, user, onAuth }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const rankIcons = { 1: "🌱", 2: "📊", 3: "🎯", 4: "🔮", 5: "🏆" };
  const rankColors = { 1: "#9B9B9B", 2: "#2563EB", 3: "#7C3AED", 4: "#B45309", 5: "#16A34A" };

  useEffect(() => {
    fetch(`/api/markets/${marketId}/comments`)
      .then(r => r.json())
      .then(d => {
        setComments(d.comments || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [marketId]);

  async function handlePost() {
    if (!user) {
      onAuth("signup");
      return;
    }
    if (!text.trim()) return;
    setPosting(true);
    try {
      const res = await fetch(`/api/markets/${marketId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: text.trim() }),
      });
      const d = await res.json();
      if (res.ok && d.comment) {
        setComments(prev => [...prev, d.comment]);
        setText("");
      }
    } catch {}
    setPosting(false);
  }

  async function handleDelete(commentId) {
    if (!confirm("Изтриване на коментара?")) return;
    await fetch(`/api/comments/${commentId}`, { method: "DELETE" });
    setComments(prev => prev.filter(c => c.id !== commentId));
  }

  const visible = showAll ? comments : comments.slice(-5);
  const isAdmin = user && (user.role === "superadmin" || user.role === "editor");

  return (
    <div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: colors.textSec,
          marginBottom: 8,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>💬 Дискусия ({comments.length})</span>
      </div>

      {loading ? (
        <div style={{ fontSize: 12, color: colors.textTer, padding: 8 }}>Зареждане...</div>
      ) : (
        <>
          {comments.length > 5 && !showAll && (
            <div
              onClick={() => setShowAll(true)}
              style={{
                fontSize: 12,
                color: colors.green,
                cursor: "pointer",
                textAlign: "center",
                padding: 6,
                marginBottom: 6,
              }}
            >
              ▲ Покажи всички {comments.length} коментара
            </div>
          )}

          {visible.length === 0 && (
            <div
              style={{
                fontSize: 12,
                color: colors.textTer,
                textAlign: "center",
                padding: "12px 0",
              }}
            >
              Все още няма коментари. Бъдете първи!
            </div>
          )}

          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {visible.map(c => {
              const ri = rankIcons[c.rank_id] || "🌱";
              const rc = rankColors[c.rank_id] || "#9B9B9B";
              const isOwn = user && user.id === c.user_id;
              const canDelete = isOwn || isAdmin;
              const date = new Date(c.created_at);
              const timeStr =
                date.toLocaleDateString("bg-BG", { day: "numeric", month: "short" }) +
                " " +
                date.toLocaleTimeString("bg-BG", { hour: "2-digit", minute: "2-digit" });

              return (
                <div
                  key={c.id}
                  style={{ padding: "8px 0", borderBottom: `1px solid ${colors.border}` }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 3,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 12 }}>{ri}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: rc }}>
                        @{c.username}
                      </span>
                      <span style={{ fontSize: 10, color: colors.textTer }}>{timeStr}</span>
                    </div>
                    {canDelete && (
                      <span
                        onClick={() => handleDelete(c.id)}
                        style={{
                          fontSize: 11,
                          color: colors.textTer,
                          cursor: "pointer",
                          padding: "2px 6px",
                        }}
                        title="Изтрий"
                      >
                        ×
                      </span>
                    )}
                  </div>
                  <div
                    style={{ fontSize: 13, lineHeight: 1.5, color: colors.text, paddingLeft: 22 }}
                  >
                    {c.body}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Comment input */}
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <input
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handlePost();
                }
              }}
              placeholder={user ? "Напишете коментар..." : "Влезте, за да коментирате"}
              maxLength={1000}
              disabled={!user}
              style={{
                flex: 1,
                padding: "8px 12px",
                fontSize: 13,
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                outline: "none",
                fontFamily: fonts.body,
                background: user ? colors.card : colors.bg,
              }}
            />
            <button
              onClick={user ? handlePost : () => onAuth("signup")}
              disabled={posting || (user && !text.trim())}
              style={{
                padding: "8px 14px",
                fontSize: 13,
                fontWeight: 600,
                border: "none",
                borderRadius: 8,
                background: colors.green,
                color: "#FFF",
                cursor: posting ? "wait" : "pointer",
                fontFamily: fonts.body,
                opacity: posting || (user && !text.trim()) ? 0.5 : 1,
              }}
            >
              {posting ? "..." : "Изпрати"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Embed Code ──
function EmbedCode({ marketId }) {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const embedUrl = `${window.location.origin}/embed/${marketId}`;
  const iframeCode = `<iframe src="${embedUrl}" width="100%" height="220" frameborder="0" style="border:none;border-radius:12px;max-width:480px"></iframe>`;

  function copyCode() {
    navigator.clipboard.writeText(iframeCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div style={{ padding: "0 28px 20px" }}>
      <div
        onClick={() => setShow(!show)}
        style={{
          fontSize: 12,
          color: colors.textTer,
          cursor: "pointer",
          textAlign: "center",
          padding: "6px",
          borderRadius: 6,
          border: `1px solid ${colors.border}`,
        }}
      >
        {show ? "▲ Скрий кода за вграждане" : "🔗 Вградете този пазар на вашия сайт"}
      </div>
      {show && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, color: colors.textSec, marginBottom: 6 }}>
            Копирайте този код и го поставете в HTML на вашата страница:
          </div>
          <div style={{ position: "relative" }}>
            <pre
              style={{
                background: colors.bg,
                borderRadius: 8,
                padding: "10px 12px",
                fontSize: 11,
                fontFamily: fonts.mono,
                overflowX: "auto",
                border: `1px solid ${colors.border}`,
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}
            >
              {iframeCode}
            </pre>
            <button
              onClick={copyCode}
              style={{
                position: "absolute",
                top: 6,
                right: 6,
                fontSize: 11,
                padding: "3px 8px",
                borderRadius: 4,
                border: `1px solid ${colors.border}`,
                background: copied ? colors.greenBg : colors.card,
                color: copied ? colors.green : colors.textSec,
                cursor: "pointer",
              }}
            >
              {copied ? "✓ Копирано" : "Копирай"}
            </button>
          </div>
          <div style={{ fontSize: 11, color: colors.textTer, marginTop: 6 }}>Преглед:</div>
          <div
            style={{
              marginTop: 4,
              borderRadius: 12,
              overflow: "hidden",
              border: `1px solid ${colors.border}`,
            }}
          >
            <iframe
              src={embedUrl}
              width="100%"
              height="220"
              frameBorder="0"
              style={{ border: "none", display: "block" }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Share Buttons ──
function ShareButtons({ market, history }) {
  const [copied, setCopied] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const shareUrl = `${window.location.origin}/share/${market.id}`;
  const yesP = Math.round(market.yes_probability * 100);
  const shareText = `${market.question} — ДА ${yesP}% / НЕ ${100 - yesP}% | BGPoll.org`;

  function shareTwitter() {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      "_blank"
    );
  }
  function shareFacebook() {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
      "_blank"
    );
  }
  function copyLink() {
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  }
  async function nativeShare() {
    // Generate image blob and share with File API (mobile)
    try {
      const blob = await generateImageBlob();
      if (
        blob &&
        navigator.canShare &&
        navigator.canShare({ files: [new File([blob], "bgpoll.png", { type: "image/png" })] })
      ) {
        await navigator.share({
          title: "BGPoll.org",
          text: shareText,
          url: shareUrl,
          files: [new File([blob], `bgpoll-${market.id}.png`, { type: "image/png" })],
        });
        return;
      }
    } catch {}
    // Fallback to text-only share
    if (navigator.share) {
      try {
        await navigator.share({ title: "BGPoll.org", text: shareText, url: shareUrl });
      } catch {}
    }
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  async function generateImageBlob() {
    const canvas = await renderCanvas();
    return new Promise(resolve => canvas.toBlob(resolve, "image/png"));
  }

  async function copyImage() {
    setCopied(true);
    try {
      const blob = await generateImageBlob();
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: copy link instead
      navigator.clipboard.writeText(shareUrl).catch(() => {});
      setTimeout(() => setCopied(false), 2000);
    }
  }

  async function renderCanvas() {
    const canvas = document.createElement("canvas");
    const w = 1200,
      h = 630;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    const hasChart = history && history.length > 2;

    // Background
    ctx.fillStyle = "#F8F7F4";
    ctx.fillRect(0, 0, w, h);

    // Watermark (drawn first, behind everything)
    ctx.save();
    ctx.translate(hasChart ? 300 : w / 2, h / 2);
    ctx.rotate((-15 * Math.PI) / 180);
    ctx.fillStyle = "rgba(0,0,0,0.035)";
    ctx.font = "bold 90px Georgia, serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("BGPoll.org", 0, 0);
    ctx.restore();

    // Green/red probability bar at top
    const greenW = w * market.yes_probability;
    ctx.fillStyle = "#16A34A";
    ctx.fillRect(0, 0, greenW, 10);
    ctx.fillStyle = "#DC2626";
    ctx.fillRect(greenW, 0, w - greenW, 10);

    // Question text (word-wrap)
    ctx.fillStyle = "#1A1A1A";
    ctx.font = "bold 38px Georgia, serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    const words = market.question.split(" ");
    let line = "",
      lines = [];
    const maxTextW = hasChart ? 560 : w - 120;
    for (const word of words) {
      const test = line + (line ? " " : "") + word;
      if (ctx.measureText(test).width > maxTextW && line) {
        lines.push(line);
        line = word;
      } else line = test;
    }
    if (line) lines.push(line);
    lines.forEach((l, i) => {
      ctx.fillText(l, 60, 72 + i * 48);
    });
    const textBottom = 72 + lines.length * 48 + 20;

    // YES label + percentage
    ctx.font = "bold 16px sans-serif";
    ctx.fillStyle = "#16A34A";
    ctx.fillText("ДА", 60, textBottom + 8);
    ctx.font = "bold 86px monospace";
    ctx.fillText(`${yesP}%`, 60, textBottom + 96);

    // NO label + percentage
    const noX = 320;
    ctx.font = "bold 16px sans-serif";
    ctx.fillStyle = "#DC2626";
    ctx.fillText("НЕ", noX, textBottom + 8);
    ctx.font = "bold 86px monospace";
    ctx.fillText(`${100 - yesP}%`, noX, textBottom + 96);

    // Probability bar below percentages
    const barY = textBottom + 116;
    const barW = hasChart ? 520 : w - 120;
    const barH = 10;
    ctx.fillStyle = "#E5E4E1";
    ctx.beginPath();
    ctx.roundRect(60, barY, barW, barH, 5);
    ctx.fill();
    const greenBarW = Math.max(barH, barW * market.yes_probability);
    ctx.fillStyle = "#16A34A";
    ctx.beginPath();
    ctx.roundRect(60, barY, greenBarW, barH, 5);
    ctx.fill();

    // Chart (right side) if history exists
    if (hasChart) {
      const chartX = 640,
        chartY = 40,
        chartW = 510,
        chartH = 480;
      const cPadL = 10,
        cPadR = 10,
        cPadT = 10,
        cPadB = 35;
      const cInnerW = chartW - cPadL - cPadR;
      const cInnerH = chartH - cPadT - cPadB;

      // Chart background
      ctx.fillStyle = "rgba(255,255,255,0.65)";
      ctx.beginPath();
      ctx.roundRect(chartX, chartY, chartW, chartH, 12);
      ctx.fill();
      ctx.strokeStyle = "#E5E4E1";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(chartX, chartY, chartW, chartH, 12);
      ctx.stroke();

      // Grid: 25%, 50%, 75%
      [0.25, 0.5, 0.75].forEach(v => {
        const y = chartY + cPadT + (1 - v) * cInnerH;
        ctx.setLineDash(v === 0.5 ? [6, 3] : [3, 5]);
        ctx.strokeStyle = v === 0.5 ? "rgba(0,0,0,0.12)" : "rgba(0,0,0,0.06)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(chartX + cPadL, y);
        ctx.lineTo(chartX + cPadL + cInnerW, y);
        ctx.stroke();
      });
      ctx.setLineDash([]);

      // Grid labels
      ctx.fillStyle = "#9B9B9B";
      ctx.font = "11px monospace";
      ctx.textAlign = "left";
      [0.25, 0.5, 0.75].forEach(v => {
        const y = chartY + cPadT + (1 - v) * cInnerH;
        ctx.fillText(`${Math.round(v * 100)}%`, chartX + cPadL + 4, y - 5);
      });

      const isMajYes = market.yes_probability >= 0.5;
      const lineColor = isMajYes ? "#16A34A" : "#DC2626";

      // Area fill
      ctx.beginPath();
      history.forEach((pt, i) => {
        const x = chartX + cPadL + (i / (history.length - 1)) * cInnerW;
        const y = chartY + cPadT + (1 - pt.probability) * cInnerH;
        if (i === 0) {
          ctx.moveTo(x, chartY + cPadT + cInnerH);
          ctx.lineTo(x, y);
        } else ctx.lineTo(x, y);
        if (i === history.length - 1) ctx.lineTo(x, chartY + cPadT + cInnerH);
      });
      ctx.closePath();
      const grad = ctx.createLinearGradient(0, chartY, 0, chartY + chartH);
      grad.addColorStop(0, isMajYes ? "rgba(22,163,74,0.25)" : "rgba(220,38,38,0.25)");
      grad.addColorStop(1, isMajYes ? "rgba(22,163,74,0.02)" : "rgba(220,38,38,0.02)");
      ctx.fillStyle = grad;
      ctx.fill();

      // Line
      ctx.beginPath();
      history.forEach((pt, i) => {
        const x = chartX + cPadL + (i / (history.length - 1)) * cInnerW;
        const y = chartY + cPadT + (1 - pt.probability) * cInnerH;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 3;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.stroke();

      // Current dot
      const lastPt = history[history.length - 1];
      const dotX = chartX + cPadL + cInnerW;
      const dotY = chartY + cPadT + (1 - lastPt.probability) * cInnerH;
      ctx.beginPath();
      ctx.arc(dotX, dotY, 7, 0, Math.PI * 2);
      ctx.fillStyle = lineColor;
      ctx.fill();
      ctx.strokeStyle = "#F8F7F4";
      ctx.lineWidth = 3;
      ctx.stroke();

      // Date labels
      ctx.fillStyle = "#9B9B9B";
      ctx.font = "12px monospace";
      ctx.textAlign = "center";
      [0, Math.floor(history.length / 2), history.length - 1].forEach(idx => {
        const pt = history[idx];
        if (!pt) return;
        const x = chartX + cPadL + (idx / (history.length - 1)) * cInnerW;
        const d = new Date(pt.time);
        ctx.fillText(
          d.toLocaleDateString("bg-BG", { day: "numeric", month: "short" }),
          x,
          chartY + chartH - 8
        );
      });
      ctx.textAlign = "left";
    }

    // Footer bar
    ctx.fillStyle = "rgba(0,0,0,0.05)";
    ctx.fillRect(0, h - 56, w, 56);

    // Volume (bottom left)
    ctx.fillStyle = "#6B6B6B";
    ctx.font = "500 18px monospace";
    ctx.textAlign = "left";
    ctx.fillText(`${market.volume} гласа`, 60, h - 22);

    // Logo (bottom right) — try loading the PNG, fallback to text
    try {
      const logo = await loadImage(LOGO_BLACK);
      const logoH = 140;
      const logoW = (logo.width / logo.height) * logoH;
      ctx.drawImage(logo, w - logoW - 10, h - 56 / 2 - logoH / 2, logoW, logoH);
    } catch {
      ctx.fillStyle = "#1A1A1A";
      ctx.font = "bold 24px sans-serif";
      ctx.textAlign = "right";
      ctx.fillText("BGPoll.org", w - 60, h - 22);
      ctx.textAlign = "left";
    }

    // Download
    return canvas;
  }

  async function downloadSnapshot() {
    setSnapshotting(true);
    try {
      const canvas = await renderCanvas();
      const a = document.createElement("a");
      a.download = `bgpoll-${market.id}.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
    } catch (e) {
      console.error("Snapshot error:", e);
    }
    setSnapshotting(false);
  }

  const btnStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    background: colors.card,
    color: colors.textSec,
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: 12,
    fontWeight: 500,
    transition: "all 0.15s",
  };

  return (
    <div
      className="bp-share-row"
      style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}
    >
      <span
        className="bp-share-label"
        style={{ fontSize: 11, color: colors.textTer, fontWeight: 600, textTransform: "uppercase" }}
      >
        Сподели:
      </span>
      <button
        onClick={shareTwitter}
        style={{ ...btnStyle }}
        onMouseEnter={e => (e.currentTarget.style.borderColor = "#1DA1F2")}
        onMouseLeave={e => (e.currentTarget.style.borderColor = colors.border)}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
        X / Twitter
      </button>
      <button
        onClick={shareFacebook}
        style={{ ...btnStyle }}
        onMouseEnter={e => (e.currentTarget.style.borderColor = "#1877F2")}
        onMouseLeave={e => (e.currentTarget.style.borderColor = colors.border)}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="#1877F2">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
        Facebook
      </button>
      <button onClick={copyLink} style={{ ...btnStyle }}>
        🔗 Копирай линк
      </button>
      <button
        onClick={copyImage}
        style={{
          ...btnStyle,
          background: copied ? colors.greenBg : colors.card,
          borderColor: copied ? colors.greenBorder : colors.border,
          color: copied ? colors.green : colors.textSec,
        }}
      >
        {copied ? "✓ Копирано!" : "📋 Копирай картинка"}
      </button>
      <button
        onClick={downloadSnapshot}
        disabled={snapshotting}
        style={{ ...btnStyle, opacity: snapshotting ? 0.6 : 1 }}
      >
        📸 {snapshotting ? "..." : "Изтегли картинка"}
      </button>
      {typeof navigator.share === "function" && (
        <button onClick={nativeShare} style={{ ...btnStyle }}>
          📤 Сподели...
        </button>
      )}
    </div>
  );
}

// ── Market Detail Modal with Chart ──
function MarketDetailModal({
  market: initialMarket,
  voted,
  user,
  onClose,
  onVote,
  onGuestVote,
  onAuth,
  onOpenShop,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVoting, setIsVoting] = useState(false);

  useEffect(() => {
    fetch(`/api/markets/${initialMarket.id}/history`)
      .then(r => r.json())
      .then(d => {
        setData(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [initialMarket.id]);

  const m = data?.market || initialMarket;
  const history = data?.history || [];
  const cc = getCatColors(m.category);
  const cl = getCatLabel(m.category);
  const isMajorityYes = m.yes_probability >= 0.5;
  const lineColor = isMajorityYes ? colors.green : colors.red;
  const isResolved = m.status === "final" || m.status === "voided";
  const daysLeft = Math.max(
    0,
    Math.ceil((new Date(m.end_date) - new Date()) / (1000 * 60 * 60 * 24))
  );

  async function handleLocalVote(side) {
    setIsVoting(true);
    if (user) {
      await onVote(m.id, side);
    } else {
      await onGuestVote(m.id, side);
    }
    setIsVoting(false);
    // Refresh chart data
    fetch(`/api/markets/${m.id}/history`)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => {});
  }

  // SVG Chart - responsive width
  const [chartContainer, setChartContainer] = useState(null);
  const [containerWidth, setContainerWidth] = useState(560);
  
  useEffect(() => {
    if (chartContainer) {
      const updateWidth = () => {
        const width = Math.min(chartContainer.offsetWidth - 32, 560);
        setContainerWidth(width);
      };
      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }
  }, [chartContainer]);
  
  const chartW = containerWidth;
  const chartH = 200;
  const pad = { top: 20, right: 20, bottom: 30, left: 45 };
  const innerW = chartW - pad.left - pad.right;
  const innerH = chartH - pad.top - pad.bottom;

  let pathD = "";
  let areaD = "";
  const dots = [];
  if (history.length > 1) {
    history.forEach((pt, i) => {
      const x = pad.left + (i / (history.length - 1)) * innerW;
      const y = pad.top + (1 - pt.probability) * innerH;
      pathD += `${i === 0 ? "M" : "L"} ${x} ${y} `;
      if (i === 0) areaD = `M ${x} ${pad.top + innerH} L ${x} ${y} `;
      else areaD += `L ${x} ${y} `;
      if (i === history.length - 1) areaD += `L ${x} ${pad.top + innerH} Z`;
      dots.push({ x, y, prob: pt.probability, idx: i });
    });
  }

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(6px)",
        padding: "16px",
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <div
        className="bp-modal-content"
        style={{
          background: colors.card,
          borderRadius: 16,
          width: "100%",
          maxWidth: "min(640px, 100vw - 32px)",
          boxShadow: "0 12px 48px rgba(0,0,0,0.18)",
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          position: "relative",
          margin: "auto",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Watermark */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) rotate(-25deg)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          <img src={LOGO_BLACK} alt="" style={{ width: 500, opacity: 0.06 }} />
        </div>

        <div style={{ position: "relative", zIndex: 1 }}>
          {/* Header */}
          <div style={{ padding: "24px 28px 0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: cc.bg,
                  color: cc.color,
                }}
              >
                {cl}
              </span>
              <button
                onClick={onClose}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 24,
                  color: colors.textTer,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <h2
              style={{
                fontFamily: fonts.display,
                fontSize: 22,
                fontWeight: 700,
                lineHeight: 1.3,
                marginTop: 12,
              }}
            >
              {m.question}
            </h2>
            {m.question_en && (
              <p style={{ fontSize: 13, color: colors.textTer, marginTop: 4, fontStyle: "italic" }}>
                {m.question_en}
              </p>
            )}
          </div>

          {/* Probability display */}
          <div
            className="bp-modal-pad bp-prob-row"
            style={{ padding: "20px 28px", display: "flex", gap: 24, alignItems: "center" }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: colors.textTer,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontWeight: 600,
                }}
              >
                Да
              </div>
              <div
                className="bp-prob-num"
                style={{
                  fontFamily: fonts.mono,
                  fontSize: 36,
                  fontWeight: 700,
                  color: colors.green,
                  lineHeight: 1,
                }}
              >
                {Math.round(m.yes_probability * 100)}%
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: colors.textTer,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontWeight: 600,
                }}
              >
                Не
              </div>
              <div
                className="bp-prob-num"
                style={{
                  fontFamily: fonts.mono,
                  fontSize: 36,
                  fontWeight: 700,
                  color: colors.red,
                  lineHeight: 1,
                }}
              >
                {Math.round((1 - m.yes_probability) * 100)}%
              </div>
            </div>
            <div style={{ marginLeft: "auto", textAlign: "right" }}>
              <div style={{ fontFamily: fonts.mono, fontSize: 14, color: colors.textSec }}>
                {m.volume} гласа
              </div>
              {!isResolved && (
                <div style={{ fontSize: 12, color: daysLeft <= 7 ? colors.red : colors.textTer }}>
                  {daysLeft} дни остават
                </div>
              )}
              {isResolved && (
                <div
                  style={{
                    fontFamily: fonts.mono,
                    fontSize: 18,
                    fontWeight: 700,
                    color: m.outcome === 1 ? colors.green : colors.red,
                    marginTop: 2,
                  }}
                >
                  Резултат: {m.outcome === 1 ? "ДА" : "НЕ"}
                </div>
              )}
            </div>
          </div>

          {/* Probability bar */}
          <div style={{ padding: "0 28px", marginBottom: 4 }}>
            <div
              style={{
                width: "100%",
                height: 6,
                borderRadius: 3,
                background: colors.border,
                overflow: "hidden",
                display: "flex",
              }}
            >
              <div
                style={{
                  width: `${m.yes_probability * 100}%`,
                  background: colors.green,
                  transition: "width 0.5s",
                  borderRadius: "3px 0 0 3px",
                }}
              ></div>
              <div style={{ flex: 1, background: colors.red, borderRadius: "0 3px 3px 0" }}></div>
            </div>
          </div>

          {/* Vote split — visible to all */}
          <div style={{ padding: "0 28px", marginBottom: 4 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                color: colors.textTer,
                marginTop: 4,
              }}
            >
              <span style={{ color: colors.green, fontWeight: 600 }}>
                ДА: {Math.round(m.volume * m.yes_probability)} гласа
              </span>
              <span style={{ color: colors.red, fontWeight: 600 }}>
                НЕ: {Math.round(m.volume * (1 - m.yes_probability))} гласа
              </span>
            </div>
          </div>

          {/* Chart — visible to all */}
          <div style={{ padding: "16px 28px" }} ref={setChartContainer}>
            <div style={{ fontSize: 12, color: colors.textTer, marginBottom: 8, fontWeight: 600 }}>
              📈 Тренд на прогнозите
            </div>
            {loading ? (
              <div
                style={{
                  height: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: colors.textTer,
                }}
              >
                Зареждане...
              </div>
            ) : history.length < 2 ? (
              <div
                style={{
                  height: 120,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: colors.textTer,
                  fontSize: 13,
                }}
              >
                Недостатъчно данни за графика. Гласувайте първи!
              </div>
            ) : (
              <svg
                viewBox={`0 0 ${chartW} ${chartH}`}
                style={{ width: "100%", height: "auto", maxHeight: 220 }}
              >
                {/* Watermark in chart */}
                <image
                  href={LOGO_BLACK}
                  x={chartW / 2 - 120}
                  y={chartH / 2 - 40}
                  width="240"
                  height="80"
                  opacity="0.05"
                  transform={`rotate(-15, ${chartW / 2}, ${chartH / 2})`}
                />

                {/* Grid lines */}
                {[0, 0.25, 0.5, 0.75, 1].map(v => {
                  const y = pad.top + (1 - v) * innerH;
                  return (
                    <g key={v}>
                      <line
                        x1={pad.left}
                        y1={y}
                        x2={pad.left + innerW}
                        y2={y}
                        stroke={v === 0.5 ? "rgba(0,0,0,0.08)" : "rgba(0,0,0,0.04)"}
                        strokeDasharray={v === 0.5 ? "4 2" : "2 4"}
                      />
                      <text
                        x={pad.left - 8}
                        y={y + 4}
                        textAnchor="end"
                        fill={colors.textTer}
                        fontSize="10"
                        fontFamily={fonts.mono}
                      >
                        {Math.round(v * 100)}%
                      </text>
                    </g>
                  );
                })}

                {/* 50% label */}
                <text
                  x={pad.left + innerW + 4}
                  y={pad.top + innerH * 0.5 + 4}
                  fill={colors.textTer}
                  fontSize="9"
                  fontFamily={fonts.mono}
                >
                  50%
                </text>

                {/* Area fill */}
                <defs>
                  <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={lineColor} stopOpacity="0.15" />
                    <stop offset="100%" stopColor={lineColor} stopOpacity="0.02" />
                  </linearGradient>
                </defs>
                {areaD && <path d={areaD} fill="url(#areaGrad)" />}

                {/* Line */}
                <path
                  d={pathD}
                  fill="none"
                  stroke={lineColor}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {/* Current price dot */}
                {dots.length > 0 && (
                  <circle
                    cx={dots[dots.length - 1].x}
                    cy={dots[dots.length - 1].y}
                    r="5"
                    fill={lineColor}
                    stroke={colors.card}
                    strokeWidth="2"
                  />
                )}

                {/* X axis labels */}
                {history.length > 2 &&
                  [0, Math.floor(history.length / 2), history.length - 1].map(idx => {
                    const pt = history[idx];
                    if (!pt) return null;
                    const x = pad.left + (idx / (history.length - 1)) * innerW;
                    const date = new Date(pt.time);
                    const label = date.toLocaleDateString("bg-BG", {
                      day: "numeric",
                      month: "short",
                    });
                    return (
                      <text
                        key={idx}
                        x={x}
                        y={chartH - 8}
                        textAnchor="middle"
                        fill={colors.textTer}
                        fontSize="10"
                        fontFamily={fonts.mono}
                      >
                        {label}
                      </text>
                    );
                  })}
              </svg>
            )}
          </div>

          {/* Vote buttons or result */}
          <div style={{ padding: "0 28px 20px" }}>
            {isResolved ? (
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div
                  style={{
                    flex: 1,
                    padding: "14px",
                    borderRadius: 10,
                    background: m.outcome === 1 ? colors.greenBg : "#F9FAFB",
                    border: `2px solid ${m.outcome === 1 ? colors.green : colors.border}`,
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: 12, color: colors.textTer }}>Да</div>
                  <div
                    style={{
                      fontFamily: fonts.mono,
                      fontSize: 20,
                      fontWeight: 700,
                      color: m.outcome === 1 ? colors.green : colors.textTer,
                    }}
                  >
                    {m.outcome === 1 ? "✓ Вярно" : Math.round(m.yes_probability * 100) + "%"}
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    padding: "14px",
                    borderRadius: 10,
                    background: m.outcome === 0 ? colors.redBg : "#F9FAFB",
                    border: `2px solid ${m.outcome === 0 ? colors.red : colors.border}`,
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: 12, color: colors.textTer }}>Не</div>
                  <div
                    style={{
                      fontFamily: fonts.mono,
                      fontSize: 20,
                      fontWeight: 700,
                      color: m.outcome === 0 ? colors.red : colors.textTer,
                    }}
                  >
                    {m.outcome === 0 ? "✓ Вярно" : Math.round((1 - m.yes_probability) * 100) + "%"}
                  </div>
                </div>
              </div>
            ) : voted ? (
              <div
                style={{
                  background: colors.blueBg,
                  border: `1px solid ${colors.blueBorder}`,
                  borderRadius: 10,
                  padding: "12px 16px",
                  textAlign: "center",
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 600, color: colors.blue }}>
                  ✓ Вие гласувахте {voted === "YES" ? "ДА" : "НЕ"}
                </span>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  className="bp-modal-vote-btn"
                  onClick={() => handleLocalVote("YES")}
                  disabled={isVoting}
                  style={{
                    flex: 1,
                    padding: "14px",
                    borderRadius: 10,
                    border: `2px solid ${colors.greenBorder}`,
                    background: colors.greenBg,
                    color: colors.green,
                    cursor: isVoting ? "wait" : "pointer",
                    fontFamily: fonts.mono,
                    fontSize: 16,
                    fontWeight: 700,
                    textAlign: "center",
                    transition: "all 0.15s",
                  }}
                >
                  Да — {Math.round(m.yes_probability * 100)}%
                </button>
                <button
                  className="bp-modal-vote-btn"
                  onClick={() => handleLocalVote("NO")}
                  disabled={isVoting}
                  style={{
                    flex: 1,
                    padding: "14px",
                    borderRadius: 10,
                    border: `2px solid ${colors.redBorder}`,
                    background: colors.redBg,
                    color: colors.red,
                    cursor: isVoting ? "wait" : "pointer",
                    fontFamily: fonts.mono,
                    fontSize: 16,
                    fontWeight: 700,
                    textAlign: "center",
                    transition: "all 0.15s",
                  }}
                >
                  Не — {Math.round((1 - m.yes_probability) * 100)}%
                </button>
              </div>
            )}
          </div>

          {/* Share buttons */}
          <div style={{ padding: "0 28px 16px" }}>
            <ShareButtons market={m} history={history} />
          </div>

          {/* Market details */}
          <div style={{ padding: "0 28px 24px" }}>
            <div style={{ background: colors.bg, borderRadius: 10, padding: 16 }}>
              {m.description && (
                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textTer,
                      textTransform: "uppercase",
                      marginBottom: 4,
                    }}
                  >
                    Описание
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.5, color: colors.textSec }}>
                    {m.description}
                  </div>
                </div>
              )}
              <div
                className="bp-detail-grid"
                style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textTer,
                      textTransform: "uppercase",
                      marginBottom: 2,
                    }}
                  >
                    Крайна дата
                  </div>
                  <div style={{ fontSize: 13, color: colors.text }}>
                    {new Date(m.end_date).toLocaleDateString("bg-BG", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textTer,
                      textTransform: "uppercase",
                      marginBottom: 2,
                    }}
                  >
                    Общо гласове
                  </div>
                  <div style={{ fontSize: 13, color: colors.text, fontFamily: fonts.mono }}>
                    {data?.yes_votes || m.total_yes_votes || 0} Да /{" "}
                    {data?.no_votes || m.total_no_votes || 0} Не
                  </div>
                </div>
                {m.resolution_source && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: colors.textTer,
                        textTransform: "uppercase",
                        marginBottom: 2,
                      }}
                    >
                      Източник за решаване
                    </div>
                    <div style={{ fontSize: 13, color: colors.text }}>{m.resolution_source}</div>
                  </div>
                )}
                {m.resolution_note && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: colors.textTer,
                        textTransform: "uppercase",
                        marginBottom: 2,
                      }}
                    >
                      Бележка
                    </div>
                    <div style={{ fontSize: 13, color: colors.text }}>{m.resolution_note}</div>
                  </div>
                )}
                {m.resolution_source_url && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: colors.textTer,
                        textTransform: "uppercase",
                        marginBottom: 2,
                      }}
                    >
                      Линк към източник
                    </div>
                    <a
                      href={m.resolution_source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 13, color: colors.blue, wordBreak: "break-all" }}
                    >
                      {m.resolution_source_url}
                    </a>
                  </div>
                )}
              </div>
              <div style={{ marginTop: 12, fontSize: 11, color: colors.textTer }}>
                Създаден на{" "}
                {new Date(m.created_at).toLocaleDateString("bg-BG", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}{" "}
                · ID: {m.id}
              </div>
            </div>
          </div>

          {/* Comments section */}
          <div style={{ padding: "0 28px 20px" }}>
            <CommentsSection marketId={m.id} user={user} onAuth={onAuth} />
          </div>

          {/* Embed code */}
          <EmbedCode marketId={m.id} />
        </div>
      </div>
    </div>
  );
}

// ── First Vote Celebration ──
function FirstVoteCelebration({ celebration, onClose }) {
  const { side, market, probability } = celebration;
  const prob = Math.round((side === "YES" ? probability : 1 - probability) * 100);
  const shareText = `Направих първата си прогноза в BGPoll.org! 🗳️\n\n„${market?.question}"\nГласувах ${side === "YES" ? "ДА" : "НЕ"} при ${prob}%\n\nПрисъединете се: ${window.location.origin}`;

  function handleShare() {
    if (navigator.share) {
      navigator
        .share({ title: "BGPoll.org", text: shareText, url: window.location.origin })
        .catch(() => {});
    } else {
      navigator.clipboard
        .writeText(shareText)
        .then(() => {})
        .catch(() => {});
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(8px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#FFF",
          borderRadius: 20,
          padding: "40px 32px",
          width: "100%",
          maxWidth: 420,
          margin: "16px",
          textAlign: "center",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          position: "relative",
          overflow: "hidden",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Confetti-style decorations */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 6,
            background: "linear-gradient(90deg, #16A34A, #2563EB, #7C3AED, #D97706, #DC2626)",
          }}
        ></div>
        <div
          style={{
            position: "absolute",
            top: 12,
            left: 16,
            fontSize: 24,
            opacity: 0.3,
            transform: "rotate(-15deg)",
          }}
        >
          🎊
        </div>
        <div
          style={{
            position: "absolute",
            top: 16,
            right: 20,
            fontSize: 20,
            opacity: 0.3,
            transform: "rotate(20deg)",
          }}
        >
          🎉
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 16,
            left: 24,
            fontSize: 18,
            opacity: 0.2,
            transform: "rotate(10deg)",
          }}
        >
          ⭐
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 20,
            right: 16,
            fontSize: 22,
            opacity: 0.2,
            transform: "rotate(-10deg)",
          }}
        >
          🏆
        </div>

        <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
        <h2 style={{ fontFamily: fonts.display, fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
          Първата ви прогноза!
        </h2>
        <p style={{ fontSize: 14, color: colors.textSec, marginBottom: 16 }}>
          Добре дошли в BGPoll! Вашият глас е отчетен.
        </p>

        <div
          style={{
            background: colors.bg,
            borderRadius: 12,
            padding: "16px",
            marginBottom: 20,
            textAlign: "left",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, marginBottom: 8 }}>
            {market?.question}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                padding: "4px 12px",
                borderRadius: 6,
                background: side === "YES" ? colors.greenBg : colors.redBg,
                color: side === "YES" ? colors.green : colors.red,
                border: `1px solid ${side === "YES" ? colors.greenBorder : colors.redBorder}`,
              }}
            >
              {side === "YES" ? "ДА" : "НЕ"} @ {prob}%
            </span>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button
            onClick={handleShare}
            style={{
              flex: 1,
              padding: "12px",
              fontSize: 14,
              fontWeight: 600,
              border: "none",
              borderRadius: 10,
              background: "#1877F2",
              color: "#FFF",
              cursor: "pointer",
              fontFamily: fonts.body,
            }}
          >
            📣 Сподели
          </button>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: "12px",
              fontSize: 14,
              fontWeight: 600,
              border: `1px solid ${colors.border}`,
              borderRadius: 10,
              background: colors.card,
              color: colors.text,
              cursor: "pointer",
              fontFamily: fonts.body,
            }}
          >
            Продължи
          </button>
        </div>

        <div style={{ fontSize: 12, color: colors.textTer }}>
          🔥 Гласувайте 3 дни поред за +5 бонус гласа!
        </div>
      </div>
    </div>
  );
}

// ── Vote Shop Modal ──
function VoteShop({ user, onClose, onPurchased }) {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetch("/api/packs")
      .then(r => r.json())
      .then(d => {
        setPacks(d.packs || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    fetch("/api/purchases")
      .then(r => r.json())
      .then(d => setHistory(d.purchases || []))
      .catch(() => {});
  }, []);

  async function handleBuy(packId) {
    setPurchasing(packId);
    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pack_id: packId }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || "Грешка");
        setPurchasing(null);
        return;
      }
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    } catch {
      alert("Мрежова грешка");
      setPurchasing(null);
    }
  }

  const packColors = {
    starter: { border: colors.border, bg: colors.card, accent: colors.textSec },
    popular: { border: colors.greenBorder, bg: colors.greenBg, accent: colors.green },
    power: { border: "#BFDBFE", bg: "#EFF6FF", accent: "#2563EB" },
    mega: { border: "#FDE68A", bg: "#FFFBEB", accent: "#D97706" },
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(6px)",
      }}
      onClick={onClose}
    >
      <div
        className="bp-modal-content"
        style={{
          background: colors.card,
          borderRadius: 16,
          width: "100%",
          maxWidth: 560,
          boxShadow: "0 12px 48px rgba(0,0,0,0.18)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ padding: "24px 28px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ fontFamily: fonts.display, fontSize: 22, fontWeight: 700 }}>
              🗳️ Купи си гласове
            </h2>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                fontSize: 24,
                color: colors.textTer,
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
          <div style={{ fontSize: 12, color: colors.textTer, marginTop: 2 }}>
            100% легално. За разлика от изборите.
          </div>
          <p style={{ fontSize: 13, color: colors.textSec, marginTop: 8 }}>
            Баланс:{" "}
            <span style={{ fontFamily: fonts.mono, fontWeight: 700, color: colors.green }}>
              {user.purchased_votes}
            </span>{" "}
            гласа
            {(user.purchased_votes || 0) >= 10 && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "1px 8px",
                  borderRadius: 4,
                  background: getUserTier(user.purchased_votes || 0).color + "18",
                  color: getUserTier(user.purchased_votes || 0).color,
                }}
              >
                {getUserTier(user.purchased_votes || 0).icon}{" "}
                {getUserTier(user.purchased_votes || 0).name}
              </span>
            )}
          </p>

          {/* Tier progress */}
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
              {TIERS.filter(t => t.threshold > 0).map(t => {
                const has = (user.purchased_votes || 0) >= t.threshold;
                return (
                  <div key={t.id} style={{ flex: 1, textAlign: "center" }}>
                    <div style={{ fontSize: 14, marginBottom: 2 }}>{has ? t.icon : "🔒"}</div>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: has ? t.color : colors.textTer,
                      }}
                    >
                      {t.name}
                    </div>
                    <div style={{ fontSize: 9, color: colors.textTer }}>{t.threshold}+</div>
                  </div>
                );
              })}
            </div>
            <div
              style={{
                width: "100%",
                height: 4,
                borderRadius: 2,
                background: colors.border,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: 4,
                  borderRadius: 2,
                  background: "linear-gradient(90deg, #2563EB, #7C3AED, #D97706)",
                  width: `${Math.min(100, ((user.purchased_votes || 0) / 500) * 100)}%`,
                  transition: "width 0.5s",
                }}
              ></div>
            </div>
            {(user.purchased_votes || 0) < 500 &&
              (() => {
                const next = TIERS.find(t => t.threshold > (user.purchased_votes || 0));
                const needed = next ? next.threshold - (user.purchased_votes || 0) : 0;
                return next ? (
                  <div
                    style={{
                      fontSize: 11,
                      color: colors.textSec,
                      marginTop: 4,
                      textAlign: "center",
                    }}
                  >
                    Още <span style={{ fontWeight: 700, color: next.color }}>{needed}</span> гласа
                    до{" "}
                    <span style={{ fontWeight: 600 }}>
                      {next.icon} {next.name}
                    </span>
                  </div>
                ) : null;
              })()}
          </div>
        </div>

        <div style={{ padding: "20px 28px" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 20, color: colors.textTer }}>
              Зареждане...
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {packs.map(pack => {
                const pc = packColors[pack.id] || packColors.starter;
                const priceStr = (pack.price_cents / 100).toFixed(0);
                const perVote = (pack.price_cents / pack.votes / 100).toFixed(2);
                const isBest = pack.id === "mega";
                const isPopular = pack.id === "popular";
                return (
                  <div
                    key={pack.id}
                    style={{
                      border: `2px solid ${pc.border}`,
                      borderRadius: 12,
                      padding: "16px",
                      background: pc.bg,
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {isBest && (
                      <div
                        style={{
                          position: "absolute",
                          top: -10,
                          left: "50%",
                          transform: "translateX(-50%)",
                          fontSize: 10,
                          fontWeight: 700,
                          padding: "2px 10px",
                          borderRadius: 10,
                          background: "#D97706",
                          color: "#FFF",
                          textTransform: "uppercase",
                        }}
                      >
                        Най-изгоден
                      </div>
                    )}
                    {isPopular && (
                      <div
                        style={{
                          position: "absolute",
                          top: -10,
                          left: "50%",
                          transform: "translateX(-50%)",
                          fontSize: 10,
                          fontWeight: 700,
                          padding: "2px 10px",
                          borderRadius: 10,
                          background: colors.green,
                          color: "#FFF",
                          textTransform: "uppercase",
                        }}
                      >
                        Популярен
                      </div>
                    )}

                    <div
                      style={{ fontSize: 13, fontWeight: 700, color: pc.accent, marginBottom: 4 }}
                    >
                      {pack.name}
                    </div>
                    <div
                      style={{
                        fontFamily: fonts.mono,
                        fontSize: 28,
                        fontWeight: 700,
                        color: colors.text,
                        lineHeight: 1,
                      }}
                    >
                      {pack.votes}
                    </div>
                    <div style={{ fontSize: 11, color: colors.textTer, marginBottom: 8 }}>
                      гласа
                    </div>

                    <div
                      style={{
                        fontFamily: fonts.mono,
                        fontSize: 20,
                        fontWeight: 700,
                        color: pc.accent,
                      }}
                    >
                      €{priceStr}
                    </div>
                    <div style={{ fontSize: 11, color: colors.textTer, marginBottom: 4 }}>
                      €{perVote} / глас
                    </div>

                    {/* What this pack unlocks */}
                    {pack.votes >= 500 && (
                      <div
                        style={{ fontSize: 10, color: "#D97706", fontWeight: 600, marginBottom: 8 }}
                      >
                        💎 Отключва Олигарх — ∞ гласове/ден
                      </div>
                    )}
                    {pack.votes >= 100 && pack.votes < 500 && (
                      <div
                        style={{ fontSize: 10, color: "#7C3AED", fontWeight: 600, marginBottom: 8 }}
                      >
                        🔑 Отключва Инсайдер — графика + анализ
                      </div>
                    )}
                    {pack.votes < 100 && (
                      <div
                        style={{ fontSize: 10, color: "#2563EB", fontWeight: 600, marginBottom: 8 }}
                      >
                        ⚡ Отключва Активен — ДА/НЕ разпределение
                      </div>
                    )}

                    <button
                      onClick={() => handleBuy(pack.id)}
                      disabled={purchasing === pack.id}
                      style={{
                        width: "100%",
                        padding: "10px",
                        fontSize: 13,
                        fontWeight: 600,
                        border: "none",
                        borderRadius: 8,
                        background: pc.accent,
                        color: "#FFF",
                        cursor: purchasing === pack.id ? "wait" : "pointer",
                        fontFamily: fonts.body,
                        opacity: purchasing === pack.id ? 0.6 : 1,
                        marginTop: "auto",
                      }}
                    >
                      {purchasing === pack.id ? "..." : "Купи"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Purchase history */}
        <div style={{ padding: "0 28px 24px" }}>
          <div
            onClick={() => setShowHistory(!showHistory)}
            style={{
              fontSize: 12,
              color: colors.textTer,
              cursor: "pointer",
              textAlign: "center",
              padding: "8px",
              borderRadius: 6,
              border: `1px solid ${colors.border}`,
            }}
          >
            {showHistory ? "▲ Скрий историята" : "▼ История на покупките"}
          </div>
          {showHistory && (
            <div style={{ marginTop: 8 }}>
              {history.length === 0 ? (
                <div
                  style={{ fontSize: 12, color: colors.textTer, textAlign: "center", padding: 12 }}
                >
                  Няма покупки все още.
                </div>
              ) : (
                history.map(p => (
                  <div
                    key={p.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 0",
                      borderBottom: `1px solid ${colors.border}`,
                      fontSize: 12,
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: 600 }}>{p.votes} гласа</span>
                      <span style={{ color: colors.textTer }}>
                        {" "}
                        · €{(p.amount_cents / 100).toFixed(2)}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 11, color: colors.textTer }}>
                        {new Date(p.created_at).toLocaleDateString("bg-BG")}
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: "1px 6px",
                          borderRadius: 3,
                          background: p.status === "completed" ? colors.greenBg : "#FEF3C7",
                          color: p.status === "completed" ? colors.green : "#D97706",
                        }}
                      >
                        {p.status === "completed" ? "✓" : "⏳"}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div
          style={{
            padding: "0 28px 20px",
            fontSize: 11,
            color: colors.textTer,
            textAlign: "center",
          }}
        >
          Закупените гласове никога не изтичат. Чувал с картофи не е включен. Плащането е защитено
          от Stripe.
        </div>
      </div>
    </div>
  );
}

// ── Monthly Raffle Banner ──
function RaffleBanner({ user, onAuth }) {
  const [showRules, setShowRules] = useState(false);
  const now = new Date();
  const monthName = now.toLocaleDateString("bg-BG", { month: "long" });
  const daysLeft = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate() - now.getDate();
  const userVotes = user ? user.total_votes_this_month || user.total_votes || 0 : 0;
  const threshold = 30;
  const qualified = userVotes >= threshold;
  const progress = Math.min(userVotes / threshold, 1);

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)",
        borderRadius: 12,
        border: "1px solid #F59E0B",
        padding: "16px 20px",
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#92400E" }}>
            🎁 Томбола {monthName}
          </div>
          <div style={{ fontSize: 12, color: "#A16207", marginTop: 2 }}>
            3 награди за най-активните! Гласувай 30+ пъти този месец, за да участваш.
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
          <div style={{ fontSize: 11, color: "#A16207", whiteSpace: "nowrap" }}>
            {daysLeft} дни остават
          </div>
          <span
            onClick={() => setShowRules(!showRules)}
            style={{
              fontSize: 11,
              color: "#92400E",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {showRules ? "Скрий правила" : "Правила"}
          </span>
        </div>
      </div>

      {showRules && (
        <div
          style={{
            marginTop: 12,
            padding: 16,
            background: "rgba(255,255,255,0.7)",
            borderRadius: 10,
            fontSize: 12,
            color: "#78350F",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 13 }}>
            📋 Правила и награди
          </div>

          {/* Prize cards — ascending order */}
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
            {/* 3rd place */}
            <div
              style={{
                flex: 1,
                minWidth: 140,
                background: "#FFF",
                borderRadius: 10,
                border: "1px solid #E5E4E1",
                padding: "14px 12px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 28, marginBottom: 4 }}>🗳️</div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#A16207",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 2,
                }}
              >
                3-та награда
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#78350F" }}>500 гласа</div>
              <div style={{ fontSize: 10, color: "#A16207", marginTop: 2 }}>
                Бонус гласове в BGPoll
              </div>
            </div>

            {/* 2nd place */}
            <div
              style={{
                flex: 1,
                minWidth: 140,
                background: "#FFF",
                borderRadius: 10,
                border: "1px solid #D4D4D4",
                padding: "14px 12px",
                textAlign: "center",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              <div style={{ fontSize: 28, marginBottom: 4 }}>🎫</div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#6B7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 2,
                }}
              >
                🥈 2-ра награда
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#374151" }}>Ваучер €10</div>
              <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>
                Подаръчен ваучер по избор
              </div>
            </div>

            {/* 1st place */}
            <div
              style={{
                flex: 1,
                minWidth: 140,
                background: "linear-gradient(135deg, #FEF9C3 0%, #FDE68A 100%)",
                borderRadius: 10,
                border: "2px solid #F59E0B",
                padding: "14px 12px",
                textAlign: "center",
                boxShadow: "0 2px 8px rgba(245,158,11,0.2)",
              }}
            >
              <div style={{ fontSize: 28, marginBottom: 4 }}>🎟️</div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#92400E",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 2,
                }}
              >
                🥇 1-ва награда
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#78350F" }}>
                Билети за събитие
              </div>
              <div style={{ fontSize: 10, color: "#92400E", marginTop: 2 }}>
                Концерт, мач или шоу
              </div>
            </div>
          </div>

          {/* Rules text */}
          <div style={{ lineHeight: 1.7, fontSize: 11.5, color: "#92400E" }}>
            <div>
              ✅ Гласувай <strong>30+ пъти</strong> през месеца и автоматично участваш.
            </div>
            <div>
              ✅ На <strong>1-во число</strong> се теглят 3 победители на случаен принцип.
            </div>
            <div>✅ Всички гласове се броят — ДА, НЕ, безплатни и закупени.</div>
            <div>✅ Победителите се обявяват в сайта и получават имейл.</div>
            <div style={{ marginTop: 6, fontSize: 10, color: "#A16207" }}>
              Организаторът може да променя наградите с предварително уведомление. BGPoll.org е
              безплатна игра — не се залагат истински пари.
            </div>
          </div>
        </div>
      )}

      {user ? (
        <div style={{ marginTop: 10 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 11,
              color: "#92400E",
              fontWeight: 600,
              marginBottom: 3,
            }}
          >
            <span>
              {qualified ? "✅ Участваш в томболата!" : `${userVotes} / ${threshold} гласа`}
            </span>
            <span>{qualified ? "🎉" : `Още ${threshold - userVotes}`}</span>
          </div>
          <div
            style={{
              height: 6,
              borderRadius: 3,
              background: "rgba(146,64,14,0.15)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${progress * 100}%`,
                height: "100%",
                borderRadius: 3,
                background: qualified ? "#16A34A" : "#F59E0B",
                transition: "width 0.3s",
              }}
            ></div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => onAuth("signup")}
          style={{
            marginTop: 8,
            fontSize: 12,
            fontWeight: 600,
            padding: "6px 14px",
            border: "none",
            borderRadius: 6,
            background: "#92400E",
            color: "#FFF",
            cursor: "pointer",
          }}
        >
          Регистрирай се и участвай
        </button>
      )}
    </div>
  );
}

// ── Daily Poll ──
function DailyPoll({ user, onAuth }) {
  const [poll, setPoll] = useState(null);
  const [voted, setVoted] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/markets?category=all")
      .then(r => r.json())
      .then(d => {
        // Find a market ending soonest (proxy for daily poll)
        const live = (d.markets || []).filter(m => m.status === "live");
        const soonest = live.sort((a, b) => new Date(a.end_date) - new Date(b.end_date))[0];
        if (soonest) setPoll(soonest);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading || !poll) return null;

  const yesP = Math.round(poll.yes_probability * 100);
  const daysLeft = Math.max(0, Math.ceil((new Date(poll.end_date) - new Date()) / 86400000));

  return (
    <div
      style={{
        background: colors.card,
        borderRadius: 12,
        border: `1px solid ${colors.border}`,
        padding: "16px 20px",
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#D97706",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          ⚡ Решава се скоро
        </div>
        <div style={{ fontSize: 11, color: colors.textTer }}>
          {daysLeft <= 1 ? "Утре!" : `${daysLeft} дни`}
        </div>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, marginBottom: 10 }}>
        {poll.question}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button
          onClick={() => (user ? null : onAuth("signup"))}
          style={{
            flex: 1,
            padding: "10px",
            fontSize: 13,
            fontWeight: 700,
            border: `1.5px solid ${colors.greenBorder}`,
            borderRadius: 8,
            background: colors.greenBg,
            color: colors.green,
            cursor: "pointer",
          }}
        >
          ДА {yesP}%
        </button>
        <button
          onClick={() => (user ? null : onAuth("signup"))}
          style={{
            flex: 1,
            padding: "10px",
            fontSize: 13,
            fontWeight: 700,
            border: `1.5px solid ${colors.redBorder}`,
            borderRadius: 8,
            background: colors.redBg,
            color: colors.red,
            cursor: "pointer",
          }}
        >
          НЕ {100 - yesP}%
        </button>
      </div>
    </div>
  );
}

// ── Quiz Mode ──
function QuizModal({ markets, onClose, onAuth, user }) {
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    // Pick 5 random live markets
    const live = markets.filter(m => m.status === "live");
    const shuffled = [...live].sort(() => Math.random() - 0.5).slice(0, 5);
    setQuestions(shuffled);
  }, [markets]);

  function handleAnswer(side) {
    const q = questions[current];
    const crowdSays = q.yes_probability >= 0.5 ? "YES" : "NO";
    const agreedWithCrowd = side === crowdSays;
    const newAnswers = [
      ...answers,
      { question: q.question, side, probability: q.yes_probability, agreedWithCrowd },
    ];
    setAnswers(newAnswers);

    if (current + 1 >= questions.length) {
      setFinished(true);
    } else {
      setCurrent(current + 1);
    }
  }

  const score = answers.filter(a => a.agreedWithCrowd).length;
  const total = questions.length;

  function handleShare() {
    const text = `Направих ${score} от ${total} на BGPoll Quiz! 🧠\n\nКолко добре познаваш България?\n\n👉 bgpoll.org`;
    if (navigator.share) {
      navigator.share({ title: "BGPoll Quiz", text, url: "https://bgpoll.org" }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text).catch(() => {});
    }
  }

  if (questions.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(6px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#FFF",
          borderRadius: 16,
          padding: "28px 24px",
          width: "100%",
          maxWidth: 440,
          position: "relative",
        }}
        onClick={e => e.stopPropagation()}
      >
        {!finished ? (
          <div>
            {/* Progress */}
            <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
              {questions.map((_, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    height: 4,
                    borderRadius: 2,
                    background:
                      i < current ? colors.green : i === current ? "#D97706" : colors.border,
                  }}
                ></div>
              ))}
            </div>

            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: colors.textTer,
                textTransform: "uppercase",
                marginBottom: 8,
              }}
            >
              Въпрос {current + 1} от {total}
            </div>

            <div style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.3, marginBottom: 6 }}>
              {questions[current].question}
            </div>

            <div style={{ fontSize: 12, color: colors.textSec, marginBottom: 20 }}>
              Тълпата казва: {Math.round(questions[current].yes_probability * 100)}% ДА ·{" "}
              {questions[current].volume} гласа
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => handleAnswer("YES")}
                style={{
                  flex: 1,
                  padding: "14px",
                  fontSize: 16,
                  fontWeight: 700,
                  border: `2px solid ${colors.green}`,
                  borderRadius: 10,
                  background: colors.greenBg,
                  color: colors.green,
                  cursor: "pointer",
                }}
              >
                👍 ДА
              </button>
              <button
                onClick={() => handleAnswer("NO")}
                style={{
                  flex: 1,
                  padding: "14px",
                  fontSize: 16,
                  fontWeight: 700,
                  border: `2px solid ${colors.red}`,
                  borderRadius: 10,
                  background: colors.redBg,
                  color: colors.red,
                  cursor: "pointer",
                }}
              >
                👎 НЕ
              </button>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: "center" }}>
            {/* Results */}
            <div style={{ fontSize: 48, marginBottom: 8 }}>
              {score >= 4 ? "🏆" : score >= 3 ? "👏" : score >= 2 ? "🤔" : "😅"}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
              {score} от {total}
            </div>
            <div style={{ fontSize: 14, color: colors.textSec, marginBottom: 16 }}>
              {score >= 4
                ? "Впечатляващо! Мислиш като тълпата."
                : score >= 3
                  ? "Солидно! Имаш усет за прогнози."
                  : score >= 2
                    ? "Не е лошо, но можеш повече!"
                    : "Контра на всички? Може и ти да си прав!"}
            </div>

            {/* Answer recap */}
            <div style={{ textAlign: "left", marginBottom: 16 }}>
              {answers.map((a, i) => (
                <div
                  key={i}
                  style={{
                    padding: "8px 0",
                    borderBottom: `1px solid ${colors.border}`,
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontWeight: 600, lineHeight: 1.3 }}>{a.question}</div>
                  <div
                    style={{
                      fontSize: 12,
                      marginTop: 2,
                      color: a.agreedWithCrowd ? colors.green : colors.red,
                    }}
                  >
                    {a.agreedWithCrowd ? "✓" : "✗"} Ти: {a.side === "YES" ? "ДА" : "НЕ"} · Тълпата:{" "}
                    {Math.round(a.probability * 100)}% ДА
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={handleShare}
                style={{
                  flex: 1,
                  padding: "12px",
                  fontSize: 14,
                  fontWeight: 600,
                  border: "none",
                  borderRadius: 8,
                  background: "#1877F2",
                  color: "#FFF",
                  cursor: "pointer",
                }}
              >
                📣 Сподели
              </button>
              {!user && (
                <button
                  onClick={() => {
                    onClose();
                    onAuth("signup");
                  }}
                  style={{
                    flex: 1,
                    padding: "12px",
                    fontSize: 14,
                    fontWeight: 600,
                    border: "none",
                    borderRadius: 8,
                    background: colors.green,
                    color: "#FFF",
                    cursor: "pointer",
                  }}
                >
                  Регистрация
                </button>
              )}
              <button
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: "12px",
                  fontSize: 14,
                  fontWeight: 600,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  background: colors.card,
                  color: colors.text,
                  cursor: "pointer",
                }}
              >
                Затвори
              </button>
            </div>

            <div style={{ fontSize: 11, color: colors.textTer, marginTop: 10 }}>
              {!user
                ? "Регистрирай се и гласувай на истинските пазари!"
                : "Гласувай на истинските пазари и влез в месечната томбола!"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Барометър Dashboard ──
function BarometerPage({ onBack }) {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/markets")
      .then(r => r.json())
      .then(d => {
        setMarkets(
          (d.markets || [])
            .filter(m => m.status === "live")
            .sort((a, b) => (b.volume || 0) - (a.volume || 0))
        );
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const now = new Date();
  const dateStr = now.toLocaleDateString("bg-BG", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div
      style={{ fontFamily: fonts.body, background: "#FFF", minHeight: "100vh", color: colors.text }}
    >
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 24px 60px" }}>
        <button
          onClick={onBack}
          style={{
            fontSize: 13,
            padding: "6px 12px",
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            background: colors.card,
            cursor: "pointer",
            fontFamily: fonts.body,
            color: colors.textSec,
            marginBottom: 24,
          }}
        >
          ← Назад
        </button>

        {/* Header — designed to be screenshot-friendly */}
        <div
          style={{ marginBottom: 32, borderBottom: `3px solid ${colors.text}`, paddingBottom: 16 }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: colors.green,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  marginBottom: 4,
                }}
              >
                BGPoll.org
              </div>
              <h1
                style={{
                  fontFamily: fonts.display,
                  fontSize: 32,
                  fontWeight: 700,
                  lineHeight: 1.1,
                }}
              >
                Барометър
              </h1>
              <div style={{ fontSize: 14, color: colors.textSec, marginTop: 4 }}>
                Колективна прогноза на база {markets.reduce((s, m) => s + (m.volume || 0), 0)} гласа
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 13, color: colors.textTer }}>{dateStr}</div>
              <div style={{ fontSize: 11, color: colors.textTer }}>bgpoll.org/барометър</div>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: colors.textTer }}>
            Зареждане...
          </div>
        ) : (
          <div>
            {/* Group by category */}
            {(() => {
              const grouped = {};
              markets.forEach(m => {
                const cat = getCatLabel(m.category);
                if (!grouped[cat]) grouped[cat] = [];
                grouped[cat].push(m);
              });

              return Object.entries(grouped).map(([catName, catMarkets]) => (
                <div key={catName} style={{ marginBottom: 28 }}>
                  <h2
                    style={{
                      fontFamily: fonts.display,
                      fontSize: 16,
                      fontWeight: 700,
                      color: colors.textSec,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      marginBottom: 12,
                      paddingBottom: 6,
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    {catName}
                  </h2>

                  {catMarkets.map(m => {
                    const yesP = Math.round(m.yes_probability * 100);
                    const noP = 100 - yesP;
                    const daysLeft = Math.max(
                      0,
                      Math.ceil((new Date(m.end_date) - now) / 86400000)
                    );
                    const barColor =
                      yesP >= 70 ? colors.green : yesP <= 30 ? colors.red : "#D97706";

                    return (
                      <div
                        key={m.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "80px 1fr",
                          gap: 16,
                          alignItems: "center",
                          padding: "12px 0",
                          borderBottom: `1px solid ${colors.border}`,
                        }}
                      >
                        {/* Probability circle */}
                        <div
                          style={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            border: `3px solid ${barColor}`,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            style={{
                              fontFamily: fonts.mono,
                              fontSize: 22,
                              fontWeight: 700,
                              color: barColor,
                              lineHeight: 1,
                            }}
                          >
                            {yesP}%
                          </div>
                          <div
                            style={{
                              fontSize: 8,
                              color: colors.textTer,
                              textTransform: "uppercase",
                            }}
                          >
                            ДА
                          </div>
                        </div>

                        {/* Market info */}
                        <div>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              lineHeight: 1.3,
                              marginBottom: 4,
                            }}
                          >
                            {m.question}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 12,
                              fontSize: 11,
                              color: colors.textTer,
                            }}
                          >
                            <span>{m.volume} гласа</span>
                            <span>{daysLeft} дни остават</span>
                          </div>
                          {/* Mini bar */}
                          <div
                            style={{
                              width: "100%",
                              height: 4,
                              borderRadius: 2,
                              background: colors.border,
                              marginTop: 6,
                              overflow: "hidden",
                              display: "flex",
                            }}
                          >
                            <div
                              style={{
                                width: `${yesP}%`,
                                background: colors.green,
                                borderRadius: "2px 0 0 2px",
                              }}
                            ></div>
                            <div
                              style={{
                                flex: 1,
                                background: colors.red,
                                borderRadius: "0 2px 2px 0",
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ));
            })()}
          </div>
        )}

        {/* Footer — methodology note */}
        <div
          style={{
            marginTop: 32,
            paddingTop: 16,
            borderTop: `1px solid ${colors.border}`,
            fontSize: 11,
            color: colors.textTer,
            lineHeight: 1.6,
          }}
        >
          <strong>Методология:</strong> Вероятностите се изчисляват на база колективния вот на
          всички потребители. Всеки потребител може да гласува веднъж на пазар. Данните са в реално
          време. Пазарите се решават по данни на официални източници (ЦИК, Евростат, БФС, ATP, IWF,
          НСИ и др.). BGPoll.org е безплатна платформа за прогнозиране — не се залагат истински
          пари.
        </div>
      </div>
    </div>
  );
}

// ── Leaderboard Page ──
function LeaderboardPage({ onBack, onOpenProfile }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("accuracy");

  useEffect(() => {
    setLoading(true);
    fetch(`/api/leaderboard?sort=${sort}&limit=50`)
      .then(r => r.json())
      .then(d => {
        setData(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [sort]);

  const rankColors = { 1: "#9B9B9B", 2: "#2563EB", 3: "#7C3AED", 4: "#B45309", 5: "#16A34A" };
  const rankIcons = { 1: "🌱", 2: "📊", 3: "🎯", 4: "🔮", 5: "🏆" };
  const podiumColors = ["#FFD700", "#C0C0C0", "#CD7F32"];

  return (
    <div
      style={{
        fontFamily: fonts.body,
        background: colors.bg,
        minHeight: "100vh",
        color: colors.text,
      }}
    >
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 24px 60px" }}>
        <button
          onClick={onBack}
          style={{
            fontSize: 13,
            padding: "6px 12px",
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            background: colors.card,
            cursor: "pointer",
            fontFamily: fonts.body,
            color: colors.textSec,
            marginBottom: 24,
          }}
        >
          ← Назад
        </button>

        <h1 style={{ fontFamily: fonts.display, fontSize: 28, fontWeight: 700, marginBottom: 4 }}>
          🏆 Класация
        </h1>
        <div style={{ fontSize: 14, color: colors.textSec, marginBottom: 24 }}>
          Топ прогнозисти на BGPoll.org
        </div>

        {/* Global stats */}
        {data?.totals && (
          <div
            className="bp-stats-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 10,
              marginBottom: 24,
            }}
          >
            <div
              style={{
                background: colors.card,
                borderRadius: 10,
                border: `1px solid ${colors.border}`,
                padding: "14px 16px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontFamily: fonts.mono,
                  fontSize: 24,
                  fontWeight: 700,
                  color: colors.green,
                }}
              >
                {data.totals.users}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: colors.textTer,
                  textTransform: "uppercase",
                  marginTop: 2,
                }}
              >
                Потребители
              </div>
            </div>
            <div
              style={{
                background: colors.card,
                borderRadius: 10,
                border: `1px solid ${colors.border}`,
                padding: "14px 16px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontFamily: fonts.mono,
                  fontSize: 24,
                  fontWeight: 700,
                  color: colors.text,
                }}
              >
                {data.totals.votes}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: colors.textTer,
                  textTransform: "uppercase",
                  marginTop: 2,
                }}
              >
                Общо гласове
              </div>
            </div>
            <div
              style={{
                background: colors.card,
                borderRadius: 10,
                border: `1px solid ${colors.border}`,
                padding: "14px 16px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontFamily: fonts.mono,
                  fontSize: 24,
                  fontWeight: 700,
                  color: colors.text,
                }}
              >
                {data.totals.resolved_markets}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: colors.textTer,
                  textTransform: "uppercase",
                  marginTop: 2,
                }}
              >
                Решени пазари
              </div>
            </div>
          </div>
        )}

        {/* Rank distribution */}
        {data?.rank_distribution && (
          <div
            style={{
              background: colors.card,
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
              padding: "16px 20px",
              marginBottom: 24,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: colors.textSec, marginBottom: 10 }}>
              Разпределение по рангове
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {data.rank_distribution.map(r => (
                <div key={r.rank_id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 18 }}>{rankIcons[r.rank_id] || "🌱"}</span>
                  <span
                    style={{
                      fontFamily: fonts.mono,
                      fontSize: 16,
                      fontWeight: 700,
                      color: rankColors[r.rank_id] || "#9B9B9B",
                    }}
                  >
                    {r.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sort tabs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
          {[
            { id: "accuracy", label: "🎯 Точност" },
            { id: "brier", label: "📊 Brier Score" },
            { id: "votes", label: "🗳️ Най-активни" },
            { id: "resolved", label: "✅ Решени" },
          ].map(s => (
            <button
              key={s.id}
              onClick={() => setSort(s.id)}
              style={{
                fontSize: 13,
                fontWeight: 500,
                padding: "6px 14px",
                border: `1px solid ${sort === s.id ? colors.text : colors.border}`,
                borderRadius: 20,
                cursor: "pointer",
                background: sort === s.id ? colors.text : colors.card,
                color: sort === s.id ? "#FFF" : colors.textSec,
                fontFamily: fonts.body,
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Leaderboard table */}
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: colors.textTer }}>
            Зареждане...
          </div>
        ) : !data?.leaderboard?.length ? (
          <div
            style={{
              background: colors.card,
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
              padding: 40,
              textAlign: "center",
              color: colors.textTer,
            }}
          >
            Все още няма достатъчно данни за класация. Нужни са поне 5 решени прогнози.
          </div>
        ) : (
          <div
            style={{
              background: colors.card,
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
              overflow: "hidden",
            }}
          >
            {/* Header row */}
            <div
              className="bp-lb-table bp-lb-header"
              style={{
                display: "grid",
                gridTemplateColumns: "40px 1fr 80px 80px 80px",
                gap: 8,
                padding: "8px 16px",
                background: colors.bg,
                fontSize: 10,
                color: colors.textTer,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <span>#</span>
              <span>Потребител</span>
              <span style={{ textAlign: "center" }}>
                {sort === "accuracy"
                  ? "Точност"
                  : sort === "brier"
                    ? "Качество"
                    : sort === "votes"
                      ? "Гласове"
                      : "Решени"}
              </span>
              <span style={{ textAlign: "center" }}>Прогнози</span>
              <span style={{ textAlign: "center" }}>Ранг</span>
            </div>

            {data.leaderboard.map((u, i) => {
              const rankColor = rankColors[u.rank_id] || "#9B9B9B";
              const rankIcon = rankIcons[u.rank_id] || "🌱";
              const accuracy = Math.round((u.accuracy_90d || 0) * 100);
              const isPodium = i < 3;
              const mainValue =
                sort === "accuracy"
                  ? `${accuracy}%`
                  : sort === "brier"
                    ? (u.brier_90d || 0).toFixed(3)
                    : sort === "votes"
                      ? u.total_votes
                      : u.total_resolved;

              return (
                <div
                  className="bp-lb-table"
                  key={u.username}
                  onClick={() => onOpenProfile(u.username)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "40px 1fr 80px 80px 80px",
                    gap: 8,
                    alignItems: "center",
                    padding: "10px 16px",
                    borderBottom: `1px solid ${colors.border}`,
                    cursor: "pointer",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = colors.hover || "#F2F1EE")}
                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                >
                  {/* Position */}
                  <div
                    style={{
                      fontFamily: fonts.mono,
                      fontSize: isPodium ? 18 : 14,
                      fontWeight: 700,
                      color: isPodium ? podiumColors[i] : colors.textTer,
                    }}
                  >
                    {isPodium ? ["🥇", "🥈", "🥉"][i] : i + 1}
                  </div>

                  {/* User info */}
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      @{u.username}
                      {(u.purchased_votes || 0) >= 500 && (
                        <span title="Олигарх" style={{ marginLeft: 4 }}>
                          💎
                        </span>
                      )}
                    </div>
                    {u.display_name && u.display_name !== u.username && (
                      <div style={{ fontSize: 11, color: colors.textTer }}>{u.display_name}</div>
                    )}
                  </div>

                  {/* Main sort value */}
                  <div
                    style={{
                      textAlign: "center",
                      fontFamily: fonts.mono,
                      fontSize: 14,
                      fontWeight: 700,
                      color:
                        sort === "accuracy"
                          ? accuracy >= 60
                            ? colors.green
                            : accuracy >= 40
                              ? colors.text
                              : colors.red
                          : sort === "brier"
                            ? (u.brier_90d || 1) <= 0.18
                              ? colors.green
                              : colors.text
                            : colors.text,
                    }}
                  >
                    {mainValue}
                  </div>

                  {/* Total votes */}
                  <div
                    style={{
                      textAlign: "center",
                      fontFamily: fonts.mono,
                      fontSize: 13,
                      color: colors.textSec,
                    }}
                  >
                    {u.total_votes}
                  </div>

                  {/* Rank */}
                  <div style={{ textAlign: "center" }}>
                    <span
                      className="bp-lb-rank-col"
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: 4,
                        background: `${rankColor}15`,
                        color: rankColor,
                      }}
                    >
                      {rankIcon} {u.rank_name_bg || "Начинаещ"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: 16, fontSize: 12, color: colors.textTer }}>
          Класацията показва потребители с поне 5 решени прогнози. Данните се базират на последните
          90 дни.
        </div>
      </div>
    </div>
  );
}

// ── About Page ──
function AboutPage({ onBack }) {
  const section = {
    background: colors.card,
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
    padding: "24px 28px",
    marginBottom: 16,
  };
  const h2 = {
    fontFamily: fonts.display,
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 12,
    color: colors.text,
  };
  const h3 = {
    fontFamily: fonts.display,
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 8,
    marginTop: 16,
    color: colors.text,
  };
  const p = { fontSize: 14, lineHeight: 1.7, color: colors.textSec, marginBottom: 10 };
  const mono = {
    fontFamily: fonts.mono,
    fontSize: 13,
    background: colors.bg,
    padding: "2px 6px",
    borderRadius: 4,
  };

  return (
    <div
      style={{
        fontFamily: fonts.body,
        background: colors.bg,
        minHeight: "100vh",
        color: colors.text,
      }}
    >
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 24px 60px" }}>
        <button
          onClick={onBack}
          style={{
            fontSize: 13,
            padding: "6px 12px",
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            background: colors.card,
            cursor: "pointer",
            fontFamily: fonts.body,
            color: colors.textSec,
            marginBottom: 24,
          }}
        >
          ← Назад
        </button>

        <h1 style={{ fontFamily: fonts.display, fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
          За BGPoll.org
        </h1>
        <div style={{ fontSize: 15, color: colors.textSec, marginBottom: 24 }}>
          Безплатен пазар за прогнози за България
        </div>

        {/* What is BGPoll */}
        <div style={section}>
          <h2 style={h2}>🔮 Какво е BGPoll?</h2>
          <p style={p}>
            BGPoll.org е безплатна платформа за прогнозиране, където потребителите гласуват с „ДА"
            или „НЕ" на въпроси за бъдещето на България — политика, икономика, спорт, технологии и
            други.
          </p>
          <p style={p}>
            Всеки пазар задава въпрос (напр. „Ще има ли предсрочни парламентарни избори през 2026?")
            и показва текущата вероятност на базата на всички гласове. Когато събитието се случи или
            не, пазарът се решава и всеки глас се оценява.
          </p>
          <p style={p}>
            Целта е да се създаде колективна прогноза, която е по-точна от индивидуалното мнение.
            Без истински пари — само знание и точност.
          </p>
        </div>

        {/* How voting works */}
        <div style={section}>
          <h2 style={h2}>🗳️ Как работи гласуването?</h2>
          <p style={p}>
            Всеки потребител получава <span style={mono}>3 безплатни гласа на ден</span>.
            Неизползваните гласове не се прехвърлят — нулират се в полунощ.
          </p>
          <p style={p}>
            Когато гласувате, виждате текущата вероятност на пазара. Ако пазарът показва 70% ДА и
            30% НЕ, това означава, че мнозинството от гласувалите смятат, че ще се случи. Ранното
            гласуване носи повече точки.
          </p>
          <p style={p}>
            Можете да гласувате само веднъж на пазар. След гласуване не можете да промените
            решението си.
          </p>

          <h3 style={h3}>Закупени гласове</h3>
          <p style={p}>
            Ако искате повече от 3 гласа на ден, можете да закупите допълнителни пакети. Закупените
            гласове никога не изтичат. 100% легално — за разлика от изборите.
          </p>
          <div
            style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginTop: 8 }}
          >
            {[
              { name: "Начален", votes: 100, price: "€5", save: "" },
              { name: "Популярен", votes: 300, price: "€12", save: "20%" },
              { name: "Силен", votes: 500, price: "€18", save: "28%" },
              { name: "Олигарх", votes: "1,000", price: "€30", save: "40%" },
            ].map(pk => (
              <div
                key={pk.name}
                style={{
                  background: colors.bg,
                  borderRadius: 8,
                  padding: "10px 14px",
                  fontSize: 13,
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  {pk.name} — {pk.votes} гласа
                </div>
                <div style={{ color: colors.textSec }}>
                  {pk.price}{" "}
                  {pk.save && (
                    <span style={{ color: colors.green, fontWeight: 600 }}>(-{pk.save})</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <h3 style={h3}>Нива на достъп (Voting Power)</h3>
          <p style={p}>
            Вашият баланс от закупени гласове определя нивото ви на достъп. По-високият баланс
            отключва повече функции. Внимание — гласуването намалява баланса!
          </p>
          <div
            style={{
              borderRadius: 8,
              overflow: "hidden",
              border: `1px solid ${colors.border}`,
              marginTop: 8,
            }}
          >
            {[
              {
                icon: "🌱",
                name: "Безплатен",
                threshold: "0",
                perks: "3 гласа/ден, основен достъп",
                color: "#9B9B9B",
              },
              {
                icon: "⚡",
                name: "Активен",
                threshold: "10+",
                perks: "Виж разпределението на гласовете ДА/НЕ",
                color: "#2563EB",
              },
              {
                icon: "🔑",
                name: "Инсайдер",
                threshold: "100+",
                perks: "Графика на тренда + статистика по категории",
                color: "#7C3AED",
              },
              {
                icon: "💎",
                name: "Олигарх",
                threshold: "500+",
                perks: "Неограничени дневни гласове + 💎 флеър",
                color: "#D97706",
              },
            ].map((t, i) => (
              <div
                key={t.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 16px",
                  borderBottom: i < 3 ? `1px solid ${colors.border}` : "none",
                  background: i % 2 === 0 ? colors.card : colors.bg,
                }}
              >
                <span style={{ fontSize: 20, width: 32, textAlign: "center" }}>{t.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: t.color }}>
                    {t.name}{" "}
                    <span style={{ fontWeight: 400, color: colors.textTer }}>
                      ({t.threshold} гласа)
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: colors.textSec }}>{t.perks}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Scoring system */}
        <div style={section}>
          <h2 style={h2}>📊 Система за оценяване</h2>
          <p style={p}>
            Когато пазар бъде решен, всеки глас получава оценка. Системата измерва колко точна е
            била прогнозата ви, кога сте я направили и колко труден е бил пазарът.
          </p>

          <h3 style={h3}>Brier Score (точност)</h3>
          <p style={p}>
            Основната мярка за точност е <strong>Brier Score</strong> — колкото по-нисък, толкова
            по-добър:
          </p>
          <div
            style={{
              background: colors.bg,
              borderRadius: 8,
              padding: "12px 16px",
              fontFamily: fonts.mono,
              fontSize: 13,
              marginBottom: 10,
              textAlign: "center",
            }}
          >
            Brier = (вашата_цена − резултат)²
          </div>
          <p style={p}>
            Ако сте гласували „ДА" при 70% и резултатът е ДА (1):{" "}
            <span style={mono}>(0.70 − 1)² = 0.09</span> — отлична оценка. Ако резултатът е НЕ (0):{" "}
            <span style={mono}>(0.70 − 0)² = 0.49</span> — лоша оценка.
          </p>

          <h3 style={h3}>Foresight Weight (тежест на ранния глас)</h3>
          <p style={p}>
            Ранните гласове се ценят повече. Ако гласувате в първия ден на пазара, получавате
            множител 1.5x. В последния ден — само 0.5x.
          </p>
          <div
            style={{
              background: colors.bg,
              borderRadius: 8,
              padding: "12px 16px",
              fontFamily: fonts.mono,
              fontSize: 13,
              marginBottom: 10,
              textAlign: "center",
            }}
          >
            Foresight = 0.5 + (оставащи_дни / общо_дни)
          </div>

          <h3 style={h3}>Peer Score (сравнение с другите)</h3>
          <p style={p}>
            Вашият резултат се сравнява с медианата на всички гласували на същия пазар. Отрицателен
            peer score = по-добър от повечето.
          </p>

          <h3 style={h3}>Difficulty Multiplier (трудност)</h3>
          <p style={p}>
            Трудните пазари (където по-малко от 40% са познали) носят <span style={mono}>2x</span>{" "}
            точки. Лесните (над 80% познали) — само <span style={mono}>0.5x</span>.
          </p>
        </div>

        {/* Rank system */}
        <div style={section}>
          <h2 style={h2}>🏆 Рангове</h2>
          <p style={p}>
            Ранговете се определят от вашите резултати за последните 90 дни. По-висок ранг = повече
            привилегии.
          </p>

          <div
            style={{ borderRadius: 8, overflow: "hidden", border: `1px solid ${colors.border}` }}
          >
            {[
              {
                rank: 1,
                icon: "🌱",
                name: "Начинаещ",
                resolved: "0+",
                accuracy: "—",
                brier: "—",
                color: "#9B9B9B",
              },
              {
                rank: 2,
                icon: "📊",
                name: "Анализатор",
                resolved: "50+",
                accuracy: "55%+",
                brier: "≤0.22",
                color: "#2563EB",
              },
              {
                rank: 3,
                icon: "🎯",
                name: "Стратег",
                resolved: "200+",
                accuracy: "60%+",
                brier: "≤0.18",
                color: "#7C3AED",
              },
              {
                rank: 4,
                icon: "🔮",
                name: "Оракул",
                resolved: "500+",
                accuracy: "65%+",
                brier: "≤0.14",
                color: "#B45309",
              },
              {
                rank: 5,
                icon: "🏆",
                name: "Легенда",
                resolved: "750+",
                accuracy: "68%+",
                brier: "≤0.10",
                color: "#16A34A",
              },
            ].map((r, i) => (
              <div
                className="bp-rank-table"
                key={r.rank}
                style={{
                  display: "grid",
                  gridTemplateColumns: "40px 1fr 80px 80px 80px",
                  gap: 8,
                  alignItems: "center",
                  padding: "10px 16px",
                  background: i % 2 === 0 ? colors.card : colors.bg,
                  borderBottom: i < 4 ? `1px solid ${colors.border}` : "none",
                  fontSize: 13,
                }}
              >
                <span style={{ fontSize: 20, textAlign: "center" }}>{r.icon}</span>
                <span style={{ fontWeight: 600, color: r.color }}>{r.name}</span>
                <span
                  style={{ fontFamily: fonts.mono, color: colors.textSec, textAlign: "center" }}
                >
                  {r.resolved}
                </span>
                <span
                  style={{ fontFamily: fonts.mono, color: colors.textSec, textAlign: "center" }}
                >
                  {r.accuracy}
                </span>
                <span
                  style={{ fontFamily: fonts.mono, color: colors.textSec, textAlign: "center" }}
                >
                  {r.brier}
                </span>
              </div>
            ))}
            <div
              className="bp-rank-table"
              style={{
                display: "grid",
                gridTemplateColumns: "40px 1fr 80px 80px 80px",
                gap: 8,
                padding: "6px 16px",
                background: colors.bg,
                fontSize: 10,
                color: colors.textTer,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                borderTop: `1px solid ${colors.border}`,
              }}
            >
              <span></span>
              <span>Ранг</span>
              <span style={{ textAlign: "center" }}>Решени</span>
              <span style={{ textAlign: "center" }}>Точност</span>
              <span style={{ textAlign: "center" }}>Brier</span>
            </div>
          </div>

          <p style={{ ...p, marginTop: 12 }}>
            Ранговете се преизчисляват след всяко решаване на пазар. При 7 дни неактивност рангът
            може да спадне.
          </p>
          <p style={p}>Легенда изисква и попадане в Топ 50 национално.</p>
        </div>

        {/* How markets work */}
        <div style={section}>
          <h2 style={h2}>⚙️ Как работят пазарите?</h2>
          <p style={p}>
            Всеки пазар има въпрос, категория, крайна дата и начална вероятност от 50%. С всеки нов
            глас вероятността се преизчислява на базата на общия брой гласове „ДА" и „НЕ".
          </p>
          <p style={p}>
            Когато събитието стане ясно, администратор решава пазара като „ДА" или „НЕ" с линк към
            официален източник. Всички гласове автоматично се оценяват.
          </p>

          <h3 style={h3}>Категории</h3>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
            {_categoriesCache.map(c => (
              <span
                key={c.id}
                style={{
                  fontSize: 12,
                  padding: "4px 10px",
                  borderRadius: 6,
                  background: c.bg_color,
                  color: c.color,
                  border: `1px solid ${c.color}22`,
                  fontWeight: 600,
                }}
              >
                {c.icon} {c.label}
              </span>
            ))}
          </div>
        </div>

        {/* Methodology — academic basis */}
        <div style={section}>
          <h2 style={h2}>🔬 Методология и научна основа</h2>
          <p style={p}>
            Пазарите за прогнози (prediction markets) са инструмент за агрегиране на знания,
            изследван от десетилетия в академичната литература. Основната идея е, че събраното
            мнение на голяма група хора е систематично по-точно от мнението на отделни експерти —
            ефект, известен като „мъдростта на тълпата" (Wisdom of Crowds, James Surowiecki, 2004).
          </p>

          <h3 style={h3}>Академични доказателства</h3>
          <p style={p}>
            Iowa Electronic Markets (Университет на Айова) — работещ от 1988 г. — прогнозира
            правилно резултатите на американските президентски избори в 74% от случаите,
            превъзхождайки традиционните социологически проучвания (Berg, Nelson, Rietz, 2008).
          </p>
          <p style={p}>
            Филип Тетлок (University of Pennsylvania) демонстрира в проекта „Good Judgment Project",
            че групи от обучени прогнозисти превъзхождат разузнавателните анализатори на CIA с 30%
            (Tetlock & Gardner, „Superforecasting", 2015).
          </p>
          <p style={p}>
            Polymarket прогнозира правилно резултата от президентските избори в САЩ през 2024 г.,
            когато повечето социологически агенции бяха в грешка.
          </p>

          <h3 style={h3}>Как BGPoll изчислява вероятности</h3>
          <p style={p}>
            Всеки пазар стартира с начална вероятност. С всеки нов глас, вероятността се
            преизчислява като съотношение на гласовете „ДА" спрямо общия брой гласове. Този метод е
            прост, прозрачен и устойчив на манипулация при голям брой участници.
          </p>

          <h3 style={h3}>Оценка на точността: Brier Score</h3>
          <p style={p}>
            Brier Score е стандартна мярка за калибрираност на прогнози, въведена от Глен Бриер
            (1950) в метеорологията. Формулата е: BS = (прогноза − резултат)². Скала: 0 = перфектна
            прогноза, 0.25 = случайно познаване, 1 = напълно грешна.
          </p>
          <p style={p}>
            BGPoll допълва Brier Score с тегло за навременност (foresight weight), peer score
            (сравнение с медианата) и множител за трудност на пазара.
          </p>

          <h3 style={h3}>Ограничения</h3>
          <p style={p}>
            Пазарите за прогнози не са безгрешни. Те работят най-добре при голям брой участници и
            ликвидни пазари. При малък брой гласове, вероятностите могат да бъдат нестабилни. BGPoll
            не претендира да замени социологическите проучвания, а да ги допълни с алтернативен
            поглед.
          </p>

          <h3 style={h3}>Цитирани източници</h3>
          <div style={{ fontSize: 12, color: colors.textTer, lineHeight: 1.7 }}>
            <p style={{ marginBottom: 4 }}>
              • Surowiecki, J. (2004). The Wisdom of Crowds. Doubleday.
            </p>
            <p style={{ marginBottom: 4 }}>
              • Tetlock, P. & Gardner, D. (2015). Superforecasting: The Art and Science of
              Prediction. Crown.
            </p>
            <p style={{ marginBottom: 4 }}>
              • Berg, J., Nelson, F. & Rietz, T. (2008). Prediction market accuracy in the long run.
              International Journal of Forecasting, 24(2).
            </p>
            <p style={{ marginBottom: 4 }}>
              • Brier, G. (1950). Verification of forecasts expressed in terms of probability.
              Monthly Weather Review, 78(1).
            </p>
            <p style={{ marginBottom: 4 }}>
              • Arrow, K. et al. (2008). The promise of prediction markets. Science, 320(5878).
            </p>
          </div>
        </div>

        {/* FAQ */}
        <div style={section}>
          <h2 style={h2}>❓ Въпроси и отговори</h2>

          <h3 style={h3}>Залагат ли се истински пари?</h3>
          <p style={p}>
            Не. BGPoll е изцяло безплатна игра за прогнозиране. Единственото нещо, което се
            „залага", е вашата точност и ранг.
          </p>

          <h3 style={h3}>Мога ли да променя гласа си?</h3>
          <p style={p}>
            Не. Веднъж гласувал, не можете да промените решението си. Това гарантира честност на
            системата.
          </p>

          <h3 style={h3}>Кой решава пазарите?</h3>
          <p style={p}>
            Администраторите решават пазарите с линк към официален източник. Ако пазарът е
            неразрешим или зададен некоректно, може да бъде анулиран с възстановяване на закупени
            гласове.
          </p>

          <h3 style={h3}>Колко точни са прогнозите?</h3>
          <p style={p}>
            Колективните прогнози обикновено са по-точни от индивидуалните мнения. Колкото повече
            хора участват, толкова по-точна е общата прогноза.
          </p>

          <h3 style={h3}>Мога ли да предлагам нови пазари?</h3>
          <p style={p}>
            Засега пазарите се създават от администраторите. В бъдеще планираме да добавим
            възможност за предложения от потребителите.
          </p>
        </div>

        <div style={{ textAlign: "center", marginTop: 24, fontSize: 12, color: colors.textTer }}>
          © 2026 BGPoll.org — Народна анкета за България
        </div>
      </div>
    </div>
  );
}

// ── Profile Page ──
function ProfilePage({ username, currentUser, onBack }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`/api/profile/${username}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) setError(d.error);
        else setProfile(d.profile);
        setLoading(false);
      })
      .catch(() => {
        setError("Мрежова грешка");
        setLoading(false);
      });
  }, [username]);

  if (loading)
    return (
      <div
        style={{
          fontFamily: fonts.body,
          background: colors.bg,
          minHeight: "100vh",
          color: colors.text,
        }}
      >
        <div
          style={{
            maxWidth: 800,
            margin: "0 auto",
            padding: "40px 24px",
            textAlign: "center",
            color: colors.textTer,
          }}
        >
          Зареждане...
        </div>
      </div>
    );
  if (error)
    return (
      <div
        style={{
          fontFamily: fonts.body,
          background: colors.bg,
          minHeight: "100vh",
          color: colors.text,
        }}
      >
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 24px" }}>
          <button
            onClick={onBack}
            style={{
              fontSize: 13,
              padding: "6px 12px",
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              background: colors.card,
              cursor: "pointer",
              fontFamily: fonts.body,
              color: colors.textSec,
              marginBottom: 20,
            }}
          >
            ← Назад
          </button>
          <div style={{ textAlign: "center", color: colors.red, fontSize: 16 }}>{error}</div>
        </div>
      </div>
    );

  const p = profile;
  const isOwn = currentUser && currentUser.username === username;
  const rankColors = { 1: "#9B9B9B", 2: "#2563EB", 3: "#7C3AED", 4: "#B45309", 5: "#16A34A" };
  const rankIcons = { 1: "🌱", 2: "📊", 3: "🎯", 4: "🔮", 5: "🏆" };
  const rankColor = rankColors[p.rank_id] || "#9B9B9B";
  const rankIcon = rankIcons[p.rank_id] || "🌱";
  const accuracy = Math.round((p.accuracy_90d || 0) * 100);
  const memberSince = new Date(p.created_at).toLocaleDateString("bg-BG", {
    month: "long",
    year: "numeric",
  });

  return (
    <div
      style={{
        fontFamily: fonts.body,
        background: colors.bg,
        minHeight: "100vh",
        color: colors.text,
      }}
    >
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 24px 60px" }}>
        {/* Back button */}
        <button
          onClick={onBack}
          style={{
            fontSize: 13,
            padding: "6px 12px",
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            background: colors.card,
            cursor: "pointer",
            fontFamily: fonts.body,
            color: colors.textSec,
            marginBottom: 24,
          }}
        >
          ← Назад
        </button>

        {/* Profile header */}
        <div
          style={{
            background: colors.card,
            borderRadius: 16,
            border: `1px solid ${colors.border}`,
            overflow: "hidden",
          }}
        >
          {/* Top accent bar */}
          <div
            style={{
              height: 6,
              background: `linear-gradient(90deg, ${rankColor}, ${rankColor}88)`,
            }}
          ></div>

          <div style={{ padding: "28px 32px" }}>
            <div
              className="bp-profile-header"
              style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}
            >
              {/* Avatar circle */}
              <div
                className="bp-profile-avatar"
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: `${rankColor}18`,
                  border: `3px solid ${rankColor}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 32,
                  flexShrink: 0,
                }}
              >
                {rankIcon}
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: fonts.display, fontSize: 24, fontWeight: 700 }}>
                    @{p.username}
                  </span>
                  {isOwn && (
                    <span
                      style={{
                        fontSize: 11,
                        padding: "2px 8px",
                        borderRadius: 4,
                        background: colors.greenBg,
                        color: colors.green,
                        border: `1px solid ${colors.greenBorder}`,
                        fontWeight: 600,
                      }}
                    >
                      Вашият профил
                    </span>
                  )}
                </div>
                {p.display_name && p.display_name !== p.username && (
                  <div style={{ fontSize: 14, color: colors.textSec, marginTop: 2 }}>
                    {p.display_name}
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      padding: "3px 10px",
                      borderRadius: 6,
                      background: `${rankColor}15`,
                      color: rankColor,
                      border: `1px solid ${rankColor}30`,
                    }}
                  >
                    {rankIcon} {p.rank_name_bg || "Начинаещ"}
                  </span>
                  <span style={{ fontSize: 12, color: colors.textTer }}>Член от {memberSince}</span>
                </div>
              </div>
            </div>

            {/* Stats grid */}
            <div
              className="bp-profile-stats"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 12,
                marginTop: 24,
              }}
            >
              <div
                style={{
                  background: colors.bg,
                  borderRadius: 10,
                  padding: "14px 16px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontFamily: fonts.mono,
                    fontSize: 28,
                    fontWeight: 700,
                    color: colors.text,
                    lineHeight: 1,
                  }}
                >
                  {p.total_votes || 0}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: colors.textTer,
                    marginTop: 4,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Прогнози
                </div>
              </div>
              <div
                style={{
                  background: colors.bg,
                  borderRadius: 10,
                  padding: "14px 16px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontFamily: fonts.mono,
                    fontSize: 28,
                    fontWeight: 700,
                    color:
                      accuracy >= 60 ? colors.green : accuracy >= 40 ? colors.text : colors.red,
                    lineHeight: 1,
                  }}
                >
                  {accuracy}%
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: colors.textTer,
                    marginTop: 4,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Точност (90д)
                </div>
              </div>
              <div
                style={{
                  background: colors.bg,
                  borderRadius: 10,
                  padding: "14px 16px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontFamily: fonts.mono,
                    fontSize: 28,
                    fontWeight: 700,
                    color: colors.text,
                    lineHeight: 1,
                  }}
                >
                  {p.total_resolved || 0}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: colors.textTer,
                    marginTop: 4,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Решени
                </div>
              </div>
              <div
                style={{
                  background: colors.bg,
                  borderRadius: 10,
                  padding: "14px 16px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontFamily: fonts.mono,
                    fontSize: 28,
                    fontWeight: 700,
                    color: colors.text,
                    lineHeight: 1,
                  }}
                >
                  {(p.brier_90d || 0).toFixed(2)}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: colors.textTer,
                    marginTop: 4,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Brier (90д)
                </div>
              </div>
            </div>

            {/* Accuracy bar */}
            {p.total_resolved > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: colors.textSec }}>Точност</span>
                  <span
                    style={{
                      fontSize: 12,
                      fontFamily: fonts.mono,
                      color: accuracy >= 50 ? colors.green : colors.red,
                    }}
                  >
                    {accuracy}%
                  </span>
                </div>
                <div
                  style={{
                    width: "100%",
                    height: 6,
                    borderRadius: 3,
                    background: colors.border,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${accuracy}%`,
                      height: 6,
                      borderRadius: 3,
                      background:
                        accuracy >= 60 ? colors.green : accuracy >= 40 ? "#EAB308" : colors.red,
                      transition: "width 0.5s",
                    }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recent votes */}
        <div style={{ marginTop: 24 }}>
          <h3
            style={{ fontFamily: fonts.display, fontSize: 18, fontWeight: 700, marginBottom: 12 }}
          >
            Последни прогнози
          </h3>
          {!p.recent_votes || p.recent_votes.length === 0 ? (
            <div
              style={{
                background: colors.card,
                borderRadius: 10,
                border: `1px solid ${colors.border}`,
                padding: "24px",
                textAlign: "center",
                color: colors.textTer,
                fontSize: 14,
              }}
            >
              Няма прогнози все още.
            </div>
          ) : (
            <div
              style={{
                background: colors.card,
                borderRadius: 10,
                border: `1px solid ${colors.border}`,
                overflow: "hidden",
              }}
            >
              {p.recent_votes.map((v, i) => {
                const cc = getCatColors(v.category).color;
                const cb = getCatColors(v.category).bg;
                const cl = getCatLabel(v.category);
                const isCorrect = v.is_correct === 1;
                const isWrong = v.is_correct === 0;
                const isPending = v.is_correct === null || v.is_correct === undefined;
                const date = new Date(v.created_at).toLocaleDateString("bg-BG", {
                  day: "numeric",
                  month: "short",
                });
                const price = Math.round(v.market_price * 100);

                return (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "12px 16px",
                      borderBottom:
                        i < p.recent_votes.length - 1 ? `1px solid ${colors.border}` : "none",
                    }}
                  >
                    {/* Result indicator */}
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 14,
                        flexShrink: 0,
                        background: isCorrect ? colors.greenBg : isWrong ? colors.redBg : "#F9FAFB",
                        color: isCorrect ? colors.green : isWrong ? colors.red : colors.textTer,
                        border: `1px solid ${isCorrect ? colors.greenBorder : isWrong ? colors.redBorder : colors.border}`,
                      }}
                    >
                      {isCorrect ? "✓" : isWrong ? "✗" : "?"}
                    </div>

                    {/* Vote details */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          lineHeight: 1.3,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {v.question}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: "1px 6px",
                            borderRadius: 3,
                            background: cb,
                            color: cc,
                          }}
                        >
                          {cl}
                        </span>
                        <span style={{ fontSize: 11, color: colors.textTer }}>{date}</span>
                      </div>
                    </div>

                    {/* Vote side + price */}
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div
                        style={{
                          fontFamily: fonts.mono,
                          fontSize: 13,
                          fontWeight: 600,
                          color: v.side === "YES" ? colors.green : colors.red,
                        }}
                      >
                        {v.side === "YES" ? "ДА" : "НЕ"} @ {price}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Lifetime accuracy if available */}
        {p.lifetime_accuracy !== undefined &&
          p.lifetime_accuracy !== null &&
          p.total_resolved > 0 && (
            <div
              style={{ marginTop: 16, fontSize: 12, color: colors.textTer, textAlign: "center" }}
            >
              Точност за цялото време: {Math.round(p.lifetime_accuracy * 100)}% · Brier:{" "}
              {(p.brier_90d || 0).toFixed(3)}
            </div>
          )}
      </div>
    </div>
  );
}

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 300,
        padding: "12px 20px",
        borderRadius: 10,
        background: type === "success" ? colors.green : type === "error" ? colors.red : colors.blue,
        color: "#FFF",
        fontWeight: 600,
        fontSize: 14,
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        animation: "slideIn 0.3s ease",
      }}
    >
      {message}
      <style>{`@keyframes slideIn { from { transform: translateX(100px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
    </div>
  );
}

// ── Market Row ──
function MarketRow({ m, voted, isVoting, onVote, onAuth, user, onClick, onGuestVote }) {
  const cc = getCatColors(m.category);
  const cl = getCatLabel(m.category);
  return (
    <div
      className="bp-market-row"
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "center",
        gap: 16,
        padding: "16px 20px",
        background: voted ? "#FAFAF8" : colors.card,
        borderBottom: `1px solid ${colors.border}`,
        cursor: "pointer",
        transition: "background 0.1s",
      }}
      onMouseEnter={e => {
        if (!voted) e.currentTarget.style.background = colors.hover;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = voted ? "#FAFAF8" : colors.card;
      }}
    >
      <div>
        <div style={{ fontSize: 14.5, fontWeight: 600, lineHeight: 1.35 }}>{m.question}</div>
        <div
          style={{
            fontSize: 12,
            color: colors.textTer,
            marginTop: 4,
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              padding: "2px 6px",
              borderRadius: 4,
              background: cc.bg,
              color: cc.color,
            }}
          >
            {cl}
          </span>
          <span>
            До{" "}
            {new Date(m.end_date).toLocaleDateString("bg-BG", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
          <span>·</span>
          <span style={{ fontFamily: fonts.mono }}>{m.volume} гласа</span>
          {voted && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: 4,
                background: voted === "YES" ? colors.greenBg : colors.redBg,
                color: voted === "YES" ? colors.green : colors.red,
                border: `1px solid ${voted === "YES" ? colors.greenBorder : colors.redBorder}`,
              }}
            >
              ✓ {voted === "YES" ? "ДА" : "НЕ"}
            </span>
          )}
        </div>
      </div>
      <div
        className="bp-vote-buttons"
        style={{ display: "flex", gap: 6 }}
        onClick={e => e.stopPropagation()}
      >
        {voted ? (
          <>
            <div
              className="bp-vote-btn"
              style={{
                fontFamily: fonts.mono,
                fontSize: 13,
                fontWeight: 600,
                padding: "8px 16px",
                borderRadius: 6,
                background: voted === "YES" ? colors.green : "transparent",
                color: voted === "YES" ? "#FFF" : colors.textTer,
                border: `1px solid ${voted === "YES" ? colors.green : colors.border}`,
                minWidth: 72,
                textAlign: "center",
              }}
            >
              <span style={{ fontSize: 10, display: "block", opacity: 0.8, marginBottom: 1 }}>
                Да
              </span>
              {Math.round(m.yes_probability * 100)}%
            </div>
            <div
              className="bp-vote-btn"
              style={{
                fontFamily: fonts.mono,
                fontSize: 13,
                fontWeight: 600,
                padding: "8px 16px",
                borderRadius: 6,
                background: voted === "NO" ? colors.red : "transparent",
                color: voted === "NO" ? "#FFF" : colors.textTer,
                border: `1px solid ${voted === "NO" ? colors.red : colors.border}`,
                minWidth: 72,
                textAlign: "center",
              }}
            >
              <span style={{ fontSize: 10, display: "block", opacity: 0.8, marginBottom: 1 }}>
                Не
              </span>
              {Math.round((1 - m.yes_probability) * 100)}%
            </div>
          </>
        ) : (
          <>
            <button
              className="bp-vote-btn"
              onClick={() => (user ? onVote(m.id, "YES") : onGuestVote(m.id, "YES"))}
              disabled={isVoting}
              style={{
                fontFamily: fonts.mono,
                fontSize: 13,
                fontWeight: 600,
                padding: "8px 16px",
                borderRadius: 6,
                border: `1px solid ${colors.greenBorder}`,
                background: colors.greenBg,
                color: colors.green,
                cursor: isVoting ? "wait" : "pointer",
                minWidth: 72,
                textAlign: "center",
                opacity: isVoting ? 0.6 : 1,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  textTransform: "uppercase",
                  display: "block",
                  opacity: 0.7,
                  marginBottom: 1,
                }}
              >
                Да
              </span>
              {Math.round(m.yes_probability * 100)}%
            </button>
            <button
              className="bp-vote-btn"
              onClick={() => (user ? onVote(m.id, "NO") : onGuestVote(m.id, "NO"))}
              disabled={isVoting}
              style={{
                fontFamily: fonts.mono,
                fontSize: 13,
                fontWeight: 600,
                padding: "8px 16px",
                borderRadius: 6,
                border: `1px solid ${colors.redBorder}`,
                background: colors.redBg,
                color: colors.red,
                cursor: isVoting ? "wait" : "pointer",
                minWidth: 72,
                textAlign: "center",
                opacity: isVoting ? 0.6 : 1,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  textTransform: "uppercase",
                  display: "block",
                  opacity: 0.7,
                  marginBottom: 1,
                }}
              >
                Не
              </span>
              {Math.round((1 - m.yes_probability) * 100)}%
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ResolvedMarketRow({ m, voted, onClick }) {
  const cc = getCatColors(m.category);
  const cl = getCatLabel(m.category);
  const isYes = m.outcome === 1;
  const userCorrect = voted ? (voted === "YES" && isYes) || (voted === "NO" && !isYes) : null;
  return (
    <div
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "center",
        gap: 16,
        padding: "14px 20px",
        background: colors.card,
        borderBottom: `1px solid ${colors.border}`,
        opacity: 0.8,
        cursor: "pointer",
      }}
      onMouseEnter={e => (e.currentTarget.style.opacity = "1")}
      onMouseLeave={e => (e.currentTarget.style.opacity = "0.8")}
    >
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35 }}>{m.question}</div>
        <div
          style={{
            fontSize: 12,
            color: colors.textTer,
            marginTop: 4,
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              padding: "2px 6px",
              borderRadius: 4,
              background: cc.bg,
              color: cc.color,
            }}
          >
            {cl}
          </span>
          <span style={{ fontFamily: fonts.mono }}>{m.volume} гласа</span>
          {voted && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: 4,
                background: userCorrect ? colors.greenBg : colors.redBg,
                color: userCorrect ? colors.green : colors.red,
                border: `1px solid ${userCorrect ? colors.greenBorder : colors.redBorder}`,
              }}
            >
              {userCorrect ? "✓ Позна!" : "✗ Не позна"}
            </span>
          )}
        </div>
      </div>
      <div
        style={{
          fontFamily: fonts.mono,
          fontSize: 18,
          fontWeight: 700,
          padding: "6px 16px",
          borderRadius: 8,
          background: isYes ? colors.greenBg : colors.redBg,
          color: isYes ? colors.green : colors.red,
          border: `1px solid ${isYes ? colors.greenBorder : colors.redBorder}`,
        }}
      >
        {isYes ? "ДА" : "НЕ"}
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [authModal, setAuthModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [userVotes, setUserVotes] = useState({});
  const [votingMarket, setVotingMarket] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [showProfile, setShowProfile] = useState(null);
  const [showAbout, setShowAbout] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showBarometer, setShowBarometer] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [categories, setCategories] = useState([]);
  const [guestVotes, setGuestVotes] = useState({});
  
  // Guest onboarding states
  const [showWelcomeOverlay, setShowWelcomeOverlay] = useState(false);
  const [showGuestTips, setShowGuestTips] = useState(false);
  const [guestVoteSuccess, setGuestVoteSuccess] = useState(null);
  const [hasSeenWelcome, setHasSeenWelcome] = useState(false);

  useEffect(() => {
    fetch("/api/auth/me")
      .then(r => r.json())
      .then(d => {
        if (d.user) setUser(d.user);
      })
      .catch(() => {});
      
    // Check if this is a first-time visitor
    const hasVisited = localStorage.getItem('bgpoll_visited');
    const hasSeenWelcomeStorage = localStorage.getItem('bgpoll_seen_welcome');
    const tipsDismissed = localStorage.getItem('bgpoll_tips_dismissed');
    
    if (!hasVisited) {
      localStorage.setItem('bgpoll_visited', 'true');
      setShowWelcomeOverlay(true);
    } else if (!hasSeenWelcomeStorage) {
      if (!tipsDismissed) {
        setShowGuestTips(true);
      }
    }
    
    if (hasSeenWelcomeStorage) {
      setHasSeenWelcome(true);
    }
  }, []);
  useEffect(() => {
    document.title = "BGPoll.org — Народна анкета за България";
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = LOGO_BLACK;

    // Inject responsive CSS
    if (!document.getElementById("bgpoll-responsive")) {
      const style = document.createElement("style");
      style.id = "bgpoll-responsive";
      style.textContent = `
        /* Global mobile improvements */
        * {
          box-sizing: border-box !important;
        }
        html, body {
          overflow-x: hidden !important;
          max-width: 100vw !important;
        }
        
        @media (max-width: 640px) {
          .bp-nav-inner { padding: 0 12px !important; gap: 6px !important; }
          .bp-nav-right { gap: 6px !important; }
          .bp-nav-right .bp-hide-mobile { display: none !important; }
          .bp-nav-logo { height: 140px !important; margin-top: -50px !important; margin-bottom: -50px !important; margin-left: -32px !important; }
          .bp-leaderboard-btn { font-size: 12px !important; padding: 4px 8px !important; }
          .bp-leaderboard-btn span { display: none; }
          .bp-content { padding: 16px 12px 40px !important; }
          .bp-welcome { padding: 12px 14px !important; }
          .bp-market-row { padding: 12px 14px !important; grid-template-columns: 1fr !important; gap: 10px !important; }
          .bp-market-row .bp-vote-buttons { justify-content: flex-start; }
          .bp-vote-btn { min-width: 60px !important; padding: 6px 12px !important; font-size: 12px !important; }
          .bp-modal-content { padding: 20px 16px !important; border-radius: 12px !important; max-width: 100% !important; margin: 8px !important; }
          .bp-modal-pad { padding-left: 16px !important; padding-right: 16px !important; }
          .bp-prob-row { gap: 16px !important; }
          .bp-prob-num { font-size: 28px !important; }
          .bp-modal-vote-btn { padding: 12px !important; font-size: 14px !important; }
          .bp-share-row { gap: 4px !important; }
          .bp-share-row button { padding: 4px 8px !important; font-size: 11px !important; }
          .bp-share-row .bp-share-label { display: none !important; }
          .bp-detail-grid { grid-template-columns: 1fr !important; }
          .bp-cats-row { gap: 4px !important; }
          .bp-cats-row button { padding: 5px 10px !important; font-size: 12px !important; }
          .bp-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .bp-profile-stats { grid-template-columns: repeat(2, 1fr) !important; }
          .bp-profile-header { gap: 12px !important; }
          .bp-profile-avatar { width: 52px !important; height: 52px !important; font-size: 24px !important; }
          .bp-lb-table { grid-template-columns: 30px 1fr 60px 50px 70px !important; font-size: 12px !important; }
          .bp-lb-header { font-size: 9px !important; }
          .bp-rank-table { grid-template-columns: 30px 1fr 60px 60px 60px !important; font-size: 12px !important; }
        }
        @media (max-width: 400px) {
          .bp-nav-logo { height: 110px !important; margin-top: -38px !important; margin-bottom: -38px !important; margin-left: -26px !important; }
          .bp-prob-num { font-size: 24px !important; }
          .bp-vote-btn { min-width: 52px !important; }
          .bp-lb-table { grid-template-columns: 24px 1fr 50px 50px !important; }
          .bp-lb-rank-col { display: none !important; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);
  useEffect(() => {
    loadMarkets();
  }, []);
  useEffect(() => {
    fetch("/api/categories")
      .then(r => r.json())
      .then(d => {
        const cats = d.categories || [];
        _categoriesCache = cats;
        setCategories(cats);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!user) {
      setUserVotes({});
      return;
    }
    fetch("/api/votes/mine")
      .then(r => r.json())
      .then(d => {
        const m = {};
        (d.votes || []).forEach(v => {
          m[v.market_id] = v.side;
        });
        setUserVotes(m);
      })
      .catch(() => {});
  }, [user]);

  // Load guest votes (for non-logged-in users)
  useEffect(() => {
    if (user || markets.length === 0) return;
    markets.forEach(m => {
      fetch(`/api/votes/check/${m.id}`)
        .then(r => r.json())
        .then(d => {
          if (d.voted && d.vote) {
            setGuestVotes(prev => ({ ...prev, [m.id]: d.vote.side }));
          }
        })
        .catch(() => {});
    });
  }, [markets, user]);

  // Open market or profile from share link hash
  useEffect(() => {
    const hash = window.location.hash;
    const marketMatch = hash.match(/^#market=([\w-]+)$/);
    if (marketMatch) {
      const marketId = marketMatch[1];
      fetch(`/api/markets/${marketId}`)
        .then(r => r.json())
        .then(d => {
          if (d.market) setSelectedMarket(d.market);
        })
        .catch(() => {});
      window.history.replaceState(null, "", window.location.pathname);
    }
    const profileMatch = hash.match(/^#profile=([\w]+)$/);
    if (profileMatch) {
      setShowProfile(profileMatch[1]);
      window.history.replaceState(null, "", window.location.pathname);
    }
    if (hash === "#about") {
      setShowAbout(true);
    }
    if (hash === "#leaderboard") {
      setShowLeaderboard(true);
    }
    if (hash === "#barometer") {
      setShowBarometer(true);
    }
    if (hash === "#purchase=success") {
      // Refresh user to get updated vote count
      fetch("/api/auth/me")
        .then(r => r.json())
        .then(d => {
          if (d.user) setUser(d.user);
        })
        .catch(() => {});
      setToast({
        message: "Покупката е успешна! Гласовете са добавени към вашия акаунт.",
        type: "success",
      });
      window.history.replaceState(null, "", "/");
    }
    if (hash === "#purchase=cancel") {
      setToast({ message: "Покупката беше отказана.", type: "error" });
      window.history.replaceState(null, "", "/");
    }
    if (hash === "#shop") {
      setShowShop(true);
    }
  }, []);

  function loadMarkets() {
    fetch("/api/markets")
      .then(r => r.json())
      .then(d => {
        setMarkets(d.markets || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }
  function openProfile(username) {
    setShowProfile(username);
    window.history.pushState(null, "", `/#profile=${username}`);
  }
  function closeProfile() {
    setShowProfile(null);
    window.history.pushState(null, "", window.location.pathname);
  }

  async function handleLogout() {
    await fetch("/api/auth/logout", { method: "POST" });
    setUser(null);
    setUserVotes({});
    setShowAdmin(false);
    setShowProfile(null);
    window.history.pushState(null, "", window.location.pathname);
  }

  async function handleVote(voteDataOrMarketId, sideOrUndefined) {
    if (!user) {
      setAuthModal("signup");
      return;
    }

    // Handle both old format (marketId, side) and new format ({ market_id, side, option_id, ... })
    let voteData;
    if (typeof voteDataOrMarketId === "string" && sideOrUndefined) {
      // Old format: handleVote(marketId, side)
      voteData = { market_id: voteDataOrMarketId, side: sideOrUndefined };
    } else {
      // New format: handleVote({ market_id, side, option_id, ... })
      voteData = voteDataOrMarketId;
    }

    const marketId = voteData.market_id;
    setVotingMarket(marketId);
    
    try {
      const res = await fetch("/api/vote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(voteData),
      });
      const data = await res.json();
      if (!res.ok) {
        setToast({ message: data.error, type: "error" });
        setVotingMarket(null);
        return;
      }

      // Update user votes - store the full vote data for multi-option markets
      const voteToStore = voteData.side ? voteData.side : voteData;
      setUserVotes(prev => ({ ...prev, [marketId]: voteToStore }));
      
      setUser(prev => ({
        ...prev,
        free_votes_today: data.user.free_votes_today,
        purchased_votes: data.user.purchased_votes,
        total_votes: data.user.total_votes,
        vote_streak: data.user.vote_streak,
      }));

      // Update market data
      setMarkets(prev =>
        prev.map(m =>
          m.id !== marketId
            ? m
            : { 
                ...m, 
                yes_probability: data.market.yes_probability, 
                volume: data.market.volume,
                options: data.market.options || m.options // Update options if present
              }
        )
      );

      // First vote ever? Show celebration!
      const market = markets.find(m => m.id === marketId);
      if (data.user.total_votes === 1) {
        setCelebration({ 
          side: voteData.side || "OPTION", 
          market, 
          probability: data.market.yes_probability || 0.5 
        });
      } else {
        // Create appropriate success message based on vote type
        let message;
        if (voteData.side) {
          message = `Гласувахте ${voteData.side === "YES" ? "ДА" : "НЕ"}!`;
        } else if (voteData.option_id) {
          const option = market?.options?.find(o => o.id === voteData.option_id);
          message = `Гласувахте за "${option?.option_text || 'вашата опция'}"!`;
        } else {
          message = "Гласът ви е отчетен!";
        }
        setToast({ message, type: "success" });
      }

      // Streak bonus notification
      if (data.streak_bonus) {
        setTimeout(
          () => setToast({ message: `🔥 ${data.user.vote_streak}-дневна серия!`, type: "success" }),
          1500
        );
      }
    } catch {
      setToast({ message: "Мрежова грешка", type: "error" });
    }
    setVotingMarket(null);
  }

  async function handleGuestVote(marketId, side) {
    setVotingMarket(marketId);
    try {
      const res = await fetch("/api/vote/guest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ market_id: marketId, side }),
      });
      const data = await res.json();
      if (!res.ok) {
        setToast({ message: data.error, type: "error" });
        setVotingMarket(null);
        return;
      }

      setGuestVotes(prev => ({ ...prev, [marketId]: side }));
      const updatedMarket = markets.find(m => m.id === marketId);
      setMarkets(prev =>
        prev.map(m =>
          m.id !== marketId
            ? m
            : { ...m, yes_probability: data.market.yes_probability, volume: data.market.volume }
        )
      );
      
      // Show guest vote success notification
      setGuestVoteSuccess({
        market: updatedMarket,
        vote: { side },
        timestamp: Date.now()
      });
      
      // Hide guest tips if showing
      if (showGuestTips) {
        setShowGuestTips(false);
      }
    } catch (e) {
      console.error("Guest vote error:", e);
      setToast({ message: "Грешка при гласуване. Моля, опитайте отново.", type: "error" });
    }
    setVotingMarket(null);
  }

  const isAdmin = user && (user.role === "superadmin" || user.role === "editor");
  if (showAbout)
    return (
      <AboutPage
        onBack={() => {
          setShowAbout(false);
          window.history.pushState(null, "", "/");
        }}
      />
    );
  if (showBarometer)
    return (
      <BarometerPage
        onBack={() => {
          setShowBarometer(false);
          window.history.pushState(null, "", "/");
        }}
      />
    );
  if (showLeaderboard)
    return (
      <LeaderboardPage
        onBack={() => {
          setShowLeaderboard(false);
          window.history.pushState(null, "", "/");
        }}
        onOpenProfile={openProfile}
      />
    );
  if (showProfile)
    return <ProfilePage username={showProfile} currentUser={user} onBack={closeProfile} />;
  if (showAdmin && isAdmin)
    return (
      <AdminPanel
        user={user}
        onBack={() => {
          setShowAdmin(false);
          loadMarkets();
        }}
      />
    );

  const allFiltered = category === "all" ? markets : markets.filter(m => m.category === category);
  const activeMarkets = allFiltered.filter(m => m.status === "live");
  const resolvedMarkets = allFiltered.filter(m => m.status === "final" || m.status === "voided");

  return (
    <div
      style={{
        fontFamily: fonts.body,
        background: colors.bg,
        minHeight: "100vh",
        color: colors.text,
      }}
    >
      {authModal && (
        <AuthModal mode={authModal} onClose={() => setAuthModal(null)} onAuth={u => setUser(u)} />
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {selectedMarket && (
        <EnhancedMarketModal
          market={selectedMarket}
          userVote={userVotes[selectedMarket.id] || guestVotes[selectedMarket.id]}
          user={user}
          isGuest={!user}
          onClose={() => {
            setSelectedMarket(null);
            loadMarkets();
          }}
          onVote={(voteData) => user ? handleVote({ ...voteData, market_id: selectedMarket.id }) : handleGuestVote({ ...voteData, market_id: selectedMarket.id })}
        />
      )}
      {showShop && user && (
        <VoteShop
          user={user}
          onClose={() => setShowShop(false)}
          onPurchased={() => {
            fetch("/api/auth/me")
              .then(r => r.json())
              .then(d => {
                if (d.user) setUser(d.user);
              });
          }}
        />
      )}
      {celebration && (
        <FirstVoteCelebration celebration={celebration} onClose={() => setCelebration(null)} />
      )}
      {showQuiz && (
        <QuizModal
          markets={markets}
          onClose={() => setShowQuiz(false)}
          onAuth={setAuthModal}
          user={user}
        />
      )}

      <nav
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "rgba(248,247,244,0.85)",
          backdropFilter: "blur(16px)",
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <div
          className="bp-nav-inner"
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 24px",
            height: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img
              className="bp-nav-logo"
              src={LOGO_BLACK}
              alt="BGPoll.org"
              style={{
                height: 200,
                marginTop: -72,
                marginBottom: -72,
                marginLeft: -46,
                objectFit: "contain",
              }}
            />
          </div>
          <div className="bp-nav-right" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              className="bp-leaderboard-btn"
              onClick={() => {
                setShowLeaderboard(true);
                window.history.pushState(null, "", "/#leaderboard");
              }}
              style={{
                fontSize: 13,
                fontWeight: 600,
                padding: "6px 14px",
                borderRadius: 6,
                border: `1px solid ${colors.border}`,
                background: colors.card,
                cursor: "pointer",
                fontFamily: fonts.body,
                color: colors.text,
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = "#FFFBEB";
                e.currentTarget.style.borderColor = "#F59E0B";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = colors.card;
                e.currentTarget.style.borderColor = colors.border;
              }}
            >
              🏆 <span>Класация</span>
            </button>
            <span
              className="bp-hide-mobile"
              onClick={() => {
                setShowAbout(true);
                window.history.pushState(null, "", "/#about");
              }}
              style={{
                fontSize: 13,
                color: colors.textTer,
                cursor: "pointer",
                padding: "5px 10px",
                borderRadius: 6,
                border: `1px solid ${colors.border}`,
                fontWeight: 500,
              }}
              onMouseEnter={e => (e.currentTarget.style.color = colors.green)}
              onMouseLeave={e => (e.currentTarget.style.color = colors.textTer)}
            >
              ?
            </span>
            {user ? (
              <>
                {isAdmin && (
                  <button
                    onClick={() => setShowAdmin(true)}
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      padding: "6px 12px",
                      border: "none",
                      borderRadius: 6,
                      background: "#7C3AED",
                      color: "#FFF",
                      cursor: "pointer",
                      fontFamily: fonts.body,
                    }}
                  >
                    🔧 Админ
                  </button>
                )}
                <div
                  style={{
                    fontFamily: fonts.mono,
                    fontSize: 13,
                    color: colors.green,
                    background: colors.greenBg,
                    border: `1px solid ${colors.greenBorder}`,
                    padding: "5px 12px",
                    borderRadius: 20,
                  }}
                >
                  🗳️ {user.total_votes} прогнози
                </div>
                <div
                  onClick={() => openProfile(user.username)}
                  style={{ fontSize: 13, fontWeight: 600, cursor: "pointer", color: colors.text }}
                  onMouseEnter={e => (e.currentTarget.style.color = colors.green)}
                  onMouseLeave={e => (e.currentTarget.style.color = colors.text)}
                >
                  @{user.username}
                </div>
                <button
                  onClick={handleLogout}
                  style={{
                    fontSize: 13,
                    padding: "6px 12px",
                    border: `1px solid ${colors.border}`,
                    borderRadius: 6,
                    background: colors.card,
                    cursor: "pointer",
                    fontFamily: fonts.body,
                    color: colors.textSec,
                  }}
                >
                  Изход
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setAuthModal("login")}
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    padding: "6px 12px",
                    border: `1px solid ${colors.border}`,
                    borderRadius: 6,
                    background: colors.card,
                    cursor: "pointer",
                    fontFamily: fonts.body,
                    color: colors.textSec,
                  }}
                >
                  Вход
                </button>
                <button
                  onClick={() => setAuthModal("signup")}
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    padding: "6px 12px",
                    border: "none",
                    borderRadius: 6,
                    background: colors.text,
                    color: "#FFF",
                    cursor: "pointer",
                    fontFamily: fonts.body,
                  }}
                >
                  Регистрация
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* InfoHub.bg cross-promotion */}
      <div style={{ background: "#1a1a2e", padding: "8px 24px", textAlign: "center" }}>
        <a
          href="https://infohub.bg"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#e0e0e0", fontSize: 12, textDecoration: "none", fontFamily: fonts.body }}
        >
          📰 Анализ на резултатите в{" "}
          <span style={{ color: "#16A34A", fontWeight: 600 }}>InfoHub.bg</span> →
        </a>
      </div>

      <div
        className="bp-content"
        style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 24px 60px" }}
      >
        {/* Hero banner for non-logged-in visitors — debate-driven */}
        {!user &&
          (() => {
            const totalVotes = markets.reduce((s, m) => s + (m.volume || 0), 0);
            const hotMarket = markets
              .filter(m => m.status === "live")
              .sort((a, b) => (b.volume || 0) - (a.volume || 0))[0];
            const hotP = hotMarket ? Math.round(hotMarket.yes_probability * 100) : 50;
            return (
              <div style={{ marginBottom: 24 }}>
                {/* Main hero with live market */}
                <div
                  style={{
                    background: "#FFF",
                    borderRadius: 16,
                    border: `1px solid ${colors.border}`,
                    overflow: "hidden",
                  }}
                >
                  {/* Top accent bar */}
                  {hotMarket && (
                    <div style={{ display: "flex", height: 5 }}>
                      <div style={{ width: `${hotP}%`, background: colors.green }}></div>
                      <div style={{ flex: 1, background: colors.red }}></div>
                    </div>
                  )}

                  <div style={{ padding: "28px 28px 24px" }}>
                    {/* Social proof */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 16,
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 600, color: colors.textTer }}>
                        🗳️ {totalVotes.toLocaleString()} души вече гласуваха
                      </div>
                      <div style={{ fontSize: 11, color: colors.textTer }}>bgpoll.org</div>
                    </div>

                    {/* Hot market question */}
                    {hotMarket && (
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: colors.green,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            marginBottom: 6,
                          }}
                        >
                          🔥 Най-горещ въпрос
                        </div>
                        <h1
                          style={{
                            fontFamily: fonts.display,
                            fontSize: 26,
                            fontWeight: 700,
                            lineHeight: 1.25,
                            marginBottom: 16,
                            color: colors.text,
                          }}
                        >
                          {hotMarket.question}
                        </h1>

                        {/* Live probability display */}
                        <div
                          style={{
                            display: "flex",
                            gap: 20,
                            alignItems: "flex-end",
                            marginBottom: 12,
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: colors.green,
                                letterSpacing: "0.06em",
                              }}
                            >
                              ДА
                            </div>
                            <div
                              style={{
                                fontFamily: fonts.mono,
                                fontSize: 48,
                                fontWeight: 700,
                                color: colors.green,
                                lineHeight: 1,
                              }}
                            >
                              {hotP}%
                            </div>
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: colors.red,
                                letterSpacing: "0.06em",
                              }}
                            >
                              НЕ
                            </div>
                            <div
                              style={{
                                fontFamily: fonts.mono,
                                fontSize: 48,
                                fontWeight: 700,
                                color: colors.red,
                                lineHeight: 1,
                              }}
                            >
                              {100 - hotP}%
                            </div>
                          </div>
                          <div style={{ fontSize: 12, color: colors.textTer, paddingBottom: 6 }}>
                            {hotMarket.volume} гласа
                          </div>
                        </div>

                        {/* Probability bar */}
                        <div
                          style={{
                            display: "flex",
                            height: 8,
                            borderRadius: 4,
                            overflow: "hidden",
                            marginBottom: 20,
                          }}
                        >
                          <div style={{ width: `${hotP}%`, background: colors.green }}></div>
                          <div style={{ flex: 1, background: colors.red }}></div>
                        </div>

                        {/* The hook: agree/disagree buttons */}
                        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                          <button
                            onClick={() => setAuthModal("signup")}
                            style={{
                              flex: 1,
                              padding: "14px",
                              fontSize: 15,
                              fontWeight: 700,
                              border: `2px solid ${colors.green}`,
                              borderRadius: 10,
                              background: colors.greenBg,
                              color: colors.green,
                              cursor: "pointer",
                              fontFamily: fonts.body,
                            }}
                          >
                            👍 Съгласен съм
                          </button>
                          <button
                            onClick={() => setAuthModal("signup")}
                            style={{
                              flex: 1,
                              padding: "14px",
                              fontSize: 15,
                              fontWeight: 700,
                              border: `2px solid ${colors.red}`,
                              borderRadius: 10,
                              background: colors.redBg,
                              color: colors.red,
                              cursor: "pointer",
                              fontFamily: fonts.body,
                            }}
                          >
                            👎 Не съм съгласен
                          </button>
                        </div>

                        <div style={{ textAlign: "center", fontSize: 12, color: colors.textTer }}>
                          Регистрирайте се безплатно, за да гласувате · 🎁 10 бонус гласа при
                          регистрация
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bottom stats strip */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 20,
                    marginTop: 12,
                    fontSize: 12,
                    color: colors.textTer,
                  }}
                >
                  <span>📊 {markets.filter(m => m.status === "live").length} активни въпроса</span>
                  <span>🎯 Безплатно завинаги</span>
                  <span>🏆 Класация по точност</span>
                </div>
              </div>
            );
          })()}

        {/* Streak & welcome banner for logged-in users */}
        {user && (
          <div
            className="bp-welcome"
            style={{
              background: colors.greenBg,
              border: `1px solid ${colors.greenBorder}`,
              borderRadius: 10,
              padding: "16px 20px",
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <div
                style={{ fontSize: 15, fontWeight: 600, cursor: "pointer" }}
                onClick={() => openProfile(user.username)}
              >
                Здравейте, {user.display_name}!
              </div>
              <div style={{ fontSize: 13, color: colors.textSec, marginTop: 2 }}>
                Ранг: {user.rank_name_bg || "Начинаещ"} · {user.total_votes} прогнози ·{" "}
                {Math.round((user.accuracy_90d || 0) * 100)}% точност
                {user.tier_id && user.tier_id !== "free" && (
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: 11,
                      fontWeight: 600,
                      padding: "1px 8px",
                      borderRadius: 4,
                      background: getUserTier(user.purchased_votes || 0).color + "18",
                      color: getUserTier(user.purchased_votes || 0).color,
                    }}
                  >
                    {user.tier_icon} {user.tier_name}
                  </span>
                )}
              </div>
              {user.vote_streak > 0 && (
                <div style={{ fontSize: 12, color: "#D97706", fontWeight: 600, marginTop: 4 }}>
                  🔥 {user.vote_streak} {user.vote_streak === 1 ? "ден" : "дни"} поредни гласове
                  {user.vote_streak >= 7
                    ? " — 🎁 +15 бонус!"
                    : user.vote_streak >= 3
                      ? " — 🎁 +5 бонус!"
                      : user.vote_streak === 2
                        ? " — Още 1 ден до бонус!"
                        : ""}
                </div>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontFamily: fonts.mono,
                  fontSize: 28,
                  fontWeight: 700,
                  color: colors.green,
                  lineHeight: 1,
                }}
              >
                {user.total_votes}
              </div>
              <div style={{ fontSize: 11, color: colors.textSec }}>общо прогнози</div>
            </div>
          </div>
        )}

        {/* Raffle + Quiz + Daily Poll */}
        <RaffleBanner user={user} onAuth={setAuthModal} />

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button
            onClick={() => setShowQuiz(true)}
            style={{
              padding: "10px 18px",
              fontSize: 13,
              fontWeight: 700,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              background: colors.card,
              color: colors.text,
              cursor: "pointer",
              fontFamily: fonts.body,
            }}
          >
            🧠 Бърз куиз
          </button>
          <button
            onClick={() => {
              setShowBarometer(true);
              window.history.pushState(null, "", "/#barometer");
            }}
            style={{
              padding: "10px 18px",
              fontSize: 13,
              fontWeight: 700,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              background: colors.card,
              color: colors.text,
              cursor: "pointer",
              fontFamily: fonts.body,
            }}
          >
            📊 Барометър
          </button>
        </div>

        {!user && <DailyPoll user={user} onAuth={setAuthModal} />}

        <div
          className="bp-cats-row"
          style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}
        >
          {[
            { id: "all", label: "Всички", icon: "🔮" },
            ...categories.map(c => ({ id: c.id, label: c.label, icon: c.icon })),
          ].map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              style={{
                fontSize: 13,
                fontWeight: 500,
                padding: "6px 14px",
                border: `1px solid ${category === cat.id ? colors.text : colors.border}`,
                borderRadius: 20,
                cursor: "pointer",
                background: category === cat.id ? colors.text : colors.card,
                color: category === cat.id ? "#FFF" : colors.textSec,
                fontFamily: fonts.body,
              }}
            >
              {cat.icon} {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p style={{ color: colors.textTer }}>Зареждане...</p>
        ) : (
          <>
            <h2
              style={{ fontFamily: fonts.display, fontSize: 18, fontWeight: 700, marginBottom: 12 }}
            >
              🗳️ Активни въпроси{" "}
              <span
                style={{
                  fontFamily: fonts.mono,
                  fontSize: 13,
                  fontWeight: 500,
                  color: colors.textTer,
                }}
              >
                ({activeMarkets.length})
              </span>
            </h2>
            {activeMarkets.length === 0 ? (
              <div
                style={{
                  background: colors.card,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 10,
                  padding: 32,
                  textAlign: "center",
                  color: colors.textTer,
                  marginBottom: 32,
                }}
              >
                Няма активни въпроси.
              </div>
            ) : (
              <div
                style={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: 10,
                  overflow: "hidden",
                  marginBottom: 32,
                }}
              >
                {activeMarkets.map(m => (
                  <EnhancedMarketRow
                    key={m.id}
                    market={m}
                    userVote={userVotes[m.id] || guestVotes[m.id]}
                    onVote={(voteData) => user ? handleVote({ ...voteData, market_id: m.id }) : handleGuestVote({ ...voteData, market_id: m.id })}
                    onViewDetails={() => setSelectedMarket(m)}
                    isGuest={!user}
                  />
                ))}
              </div>
            )}

            {resolvedMarkets.length > 0 && (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 12,
                  }}
                >
                  <h2
                    style={{
                      fontFamily: fonts.display,
                      fontSize: 18,
                      fontWeight: 700,
                      color: colors.textSec,
                    }}
                  >
                    ✅ Приключили въпроси{" "}
                    <span
                      style={{
                        fontFamily: fonts.mono,
                        fontSize: 13,
                        fontWeight: 500,
                        color: colors.textTer,
                      }}
                    >
                      ({resolvedMarkets.length})
                    </span>
                  </h2>
                  <button
                    onClick={() => setShowResolved(!showResolved)}
                    style={{
                      fontSize: 13,
                      color: colors.textTer,
                      background: "none",
                      border: `1px solid ${colors.border}`,
                      borderRadius: 6,
                      padding: "4px 12px",
                      cursor: "pointer",
                      fontFamily: fonts.body,
                    }}
                  >
                    {showResolved ? "Скрий" : "Покажи"}
                  </button>
                </div>
                {showResolved && (
                  <div
                    style={{
                      border: `1px solid ${colors.border}`,
                      borderRadius: 10,
                      overflow: "hidden",
                      marginBottom: 32,
                    }}
                  >
                    {resolvedMarkets.map(m => (
                      <ResolvedMarketRow
                        key={m.id}
                        m={m}
                        voted={userVotes[m.id] || guestVotes[m.id]}
                        onClick={() => setSelectedMarket(m)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

        <div style={{ textAlign: "center", marginTop: 40, fontSize: 12, color: colors.textTer }}>
          ⚠️ BGPoll е безплатна платформа за прогнозиране. Не се залагат истински пари.
          <br />
          © 2026 BGPoll.org — Народна анкета за България
          <br />
          <span
            onClick={() => {
              setShowAbout(true);
              window.history.pushState(null, "", "/#about");
            }}
            style={{ color: colors.green, cursor: "pointer", fontWeight: 500 }}
          >
            Как работи?
          </span>
          {" · "}
          <span
            onClick={() => {
              setShowBarometer(true);
              window.history.pushState(null, "", "/#barometer");
            }}
            style={{ color: colors.green, cursor: "pointer", fontWeight: 500 }}
          >
            Барометър
          </span>
          {" · "}
          <span
            onClick={() => {
              setShowLeaderboard(true);
              window.history.pushState(null, "", "/#leaderboard");
            }}
            style={{ color: colors.green, cursor: "pointer", fontWeight: 500 }}
          >
            Класация
          </span>
          {" · "}
          <a
            href="https://infohub.bg"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: colors.green, fontWeight: 500, textDecoration: "none" }}
          >
            InfoHub.bg
          </a>
        </div>
      </div>
      
      {/* Guest Onboarding Components */}
      {showWelcomeOverlay && !user && (
        <WelcomeOverlay
          onClose={() => {
            setShowWelcomeOverlay(false);
            setHasSeenWelcome(true);
            localStorage.setItem('bgpoll_seen_welcome', 'true');
            setShowGuestTips(true);
          }}
          onSignUp={() => {
            setShowWelcomeOverlay(false);
            setHasSeenWelcome(true);
            localStorage.setItem('bgpoll_seen_welcome', 'true');
            setAuthModal("signup");
          }}
        />
      )}
      
      {showGuestTips && !user && hasSeenWelcome && (
        <GuestTips
          onClose={() => {
            setShowGuestTips(false);
            localStorage.setItem('bgpoll_tips_dismissed', 'true');
          }}
          onSignUp={() => {
            setShowGuestTips(false);
            setAuthModal("signup");
          }}
        />
      )}
      
      {guestVoteSuccess && !user && (
        <GuestVoteSuccess
          market={guestVoteSuccess.market}
          vote={guestVoteSuccess.vote}
          onClose={() => setGuestVoteSuccess(null)}
          onSignUp={() => {
            setGuestVoteSuccess(null);
            setAuthModal("signup");
          }}
          onViewResults={() => {
            setGuestVoteSuccess(null);
            if (guestVoteSuccess.market) {
              setSelectedMarket(guestVoteSuccess.market);
            }
          }}
        />
      )}
    </div>
  );
}

export default App;
